import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  WordCounter,
  AutoSave,
  ImageUploader,
  FontSizeHelper,
} from "../utils/editorUtils";
import {
  questionsAPI,
  seoAPI,
  customContentAPI,
  testPasswordsAPI,
  headerFooterAPI,
  templateAPI,
} from "../services/apiService";
import { autoRotateAPI } from "../services/apiService";
import "../components/ClassicEditor.css";
import StorageMonitor from "../components/StorageMonitor";

// Custom Classic Editor - Dựa trên WordPress Classic Editor source code
const ClassicEditor = ({ value, onChange }) => {
  const [content, setContent] = useState(value || "");
  const [isLoaded, setIsLoaded] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const autoSaveRef = useRef(null);
  const wordCounterRef = useRef(null);
  const imageUploaderRef = useRef(null);
  const fontSizeHelperRef = useRef(null);
  const fileInputRef = useRef(null);
  const valueRef = useRef(value);

  // Initialize WordPress Advanced Editor Tools utilities
  useEffect(() => {
    wordCounterRef.current = new WordCounter();
    autoSaveRef.current = new AutoSave({
      interval: 30000, // Auto-save every 30 seconds
      prefix: "wp_classic_editor_",
    });
    imageUploaderRef.current = new ImageUploader();
    fontSizeHelperRef.current = new FontSizeHelper();
  }, []);

  // Handle media upload to server (WordPress-style)
  const handleMediaUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    const newImages = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} không phải là file hình ảnh!`);
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} quá lớn! Vui lòng chọn file nhỏ hơn 10MB.`);
        continue;
      }

      try {
        setUploadProgress(((i + 0.5) / files.length) * 100);

        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/upload-image', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('Upload failed:', error);
          continue;
        }

        const data = await response.json();
        console.log('📸 Image uploaded:', data.url);
        newImages.push({
          url: data.url,
          filename: data.filename,
          size: data.size,
          uploadedAt: new Date().toISOString(),
        });

        setUploadProgress(((i + 1) / files.length) * 100);
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    setUploadedImages([...newImages, ...uploadedImages]);
    setIsUploading(false);
    setUploadProgress(0);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Insert selected image into editor
  const handleInsertImage = (imageUrl) => {
    if (editorInstanceRef.current) {
      const img = `<img src="${imageUrl}" alt="" style="max-width: 100%; height: auto;" />`;
      editorInstanceRef.current.insertContent(img);
      console.log('📸 Image inserted into editor:', imageUrl);
    }
    setShowMediaLibrary(false);
  };


  // Load TinyMCE từ CDN (miễn phí vĩnh viễn - không cần API key)
  useEffect(() => {
    // Check if TinyMCE is already loaded
    if (window.tinymce) {
      setIsLoaded(true);
      return;
    }

    // Check if script is already loading
    const existingScript = document.querySelector('script[src*="tinymce"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => setIsLoaded(true));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/tinymce@6/tinymce.min.js";
    script.async = true; // Load asynchronously để không block UI
    script.onload = () => {
      setIsLoaded(true);
      console.log("TinyMCE loaded successfully");
    };
    script.onerror = () => {
      console.error("Failed to load TinyMCE from primary CDN, trying fallback");
      // Fallback to another CDN
      const fallbackScript = document.createElement("script");
      fallbackScript.src =
        "https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.2/tinymce.min.js";
      fallbackScript.async = true;
      fallbackScript.onload = () => setIsLoaded(true);
      document.head.appendChild(fallbackScript);
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      // Cleanup auto-save
      if (autoSaveRef.current) {
        autoSaveRef.current.clear("editor_content");
      }
    };
  }, []);

  // Initialize TinyMCE khi đã load
  useEffect(() => {
    if (isLoaded && containerRef.current && !editorInstanceRef.current) {
      const editorId = `classic-editor-${Date.now()}`;
      containerRef.current.innerHTML = `<textarea id="${editorId}">${content}</textarea>`;

      // Cấu hình TinyMCE theo WordPress Advanced Editor Tools
      window.tinymce.init({
        selector: `#${editorId}`,
        height: 650,
        menubar: false,
        branding: false,
        promotion: false,
        license_key: "gpl", // Sử dụng GPL license (miễn phí vĩnh viễn)

        // Tối ưu performance
        cache_suffix: "?v=6.0.0",
        base_url: "https://cdn.jsdelivr.net/npm/tinymce@6",
        suffix: ".min",
        external_plugins: {},
        forced_root_block: "p",
        force_br_newlines: false,
        force_p_newlines: false,

        // Enable formatselect và fontsizeselect theo WordPress Advanced Editor Tools
        formats: {
          paragraph: { block: "p" },
          h1: { block: "h1" },
          h2: { block: "h2" },
          h3: { block: "h3" },
          h4: { block: "h4" },
          h5: { block: "h5" },
          h6: { block: "h6" },
          // Text alignment formats - CRITICAL for preserving alignment
          alignleft: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', styles: { textAlign: 'left' } },
          aligncenter: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', styles: { textAlign: 'center' } },
          alignright: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', styles: { textAlign: 'right' } },
          alignjustify: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', styles: { textAlign: 'justify' } },
        },

        // WordPress Advanced Editor Tools settings - simplified
        convert_fonts_to_spans: false,
        remove_linebreaks: false,

        // Plugins theo WordPress Advanced Editor Tools
        plugins: [
          "advlist",
          "autolink",
          "lists",
          "link",
          "image",
          "charmap",
          "preview",
          "anchor",
          "searchreplace",
          "visualblocks",
          "code",
          "fullscreen",
          "insertdatetime",
          "media",
          "table",
          "help",
          "wordcount",
          "paste",
          "textcolor",
          "colorpicker",
          "hr",
          "nonbreaking",
          "pagebreak",
          "save",
          "tabfocus",
          "template",
          "textpattern",
        ],

        // Toolbar với custom font size selector + Image Upload + Video Upload (fixed alignment)
        toolbar1:
          "formatselect bold italic blockquote bullist numlist link unlink image customimageupload media customvideoupload undo redo",
        toolbar2:
          "fontselect customfontsizeselect alignleft aligncenter alignright outdent indent pastetext removeformat forecolor table help",

        // Content styles - enhanced với image styling
        content_style: `
          body { 
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
            font-size: 14px; 
            line-height: 1.8; 
            color: #32373c;
            background: #fff;
            margin: 1em;
          }
          img { 
            max-width: 100%; 
            height: auto; 
            border: none;
            border-radius: 4px;
            box-shadow: none;
          }
          h1, h2, h3, h4, h5, h6 { margin: 1em 0 0.5em; }
          p { margin: 0 0 1em; }
          blockquote { 
            border-left: 4px solid #0073aa; 
            padding-left: 1em; 
            margin-left: 0; 
            font-style: italic;
          }
          
          /* Custom image upload button styling */
          .tox-tbtn[aria-label*="Upload Image"] .tox-tbtn__select-label {
            font-size: 16px !important;
          }
        `,

        // WordPress-style formats
        block_formats:
          "Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6; Preformatted=pre",

        // Font size formats - enable theo WordPress Advanced Editor Tools
        fontsize_formats:
          "8px 10px 12px 14px 16px 20px 24px 28px 32px 36px 48px 60px 72px 96px",

        // Paste settings để hỗ trợ pastetext button
        paste_enable_default_filters: true,
        paste_as_text: false,

        font_formats:
          "Arial=arial,helvetica,sans-serif;" +
          "Arial Black=arial black,avant garde;" +
          "Book Antiqua=book antiqua,palatino;" +
          "Comic Sans MS=comic sans ms,sans-serif;" +
          "Courier New=courier new,courier;" +
          "Georgia=georgia,palatino;" +
          "Helvetica=helvetica;" +
          "Impact=impact,chicago;" +
          "Symbol=symbol;" +
          "Tahoma=tahoma,arial,helvetica,sans-serif;" +
          "Terminal=terminal,monaco;" +
          "Times New Roman=times new roman,times;" +
          "Trebuchet MS=trebuchet ms,geneva;" +
          "Verdana=verdana,geneva;" +
          "Webdings=webdings;" +
          "Wingdings=wingdings,zapf dingbats",

        // Image upload handler - upload to server instead of base64
        images_upload_handler: (blobInfo, progress) => {
          return new Promise(async (resolve, reject) => {
            try {
              // Check file size (max 10MB)
              if (blobInfo.blob().size > 10 * 1024 * 1024) {
                reject("File quá lớn! Vui lòng chọn file nhỏ hơn 10MB.");
                return;
              }

              // Progress tracking
              progress(10);

              // Upload to server
              const formData = new FormData();
              formData.append('file', blobInfo.blob(), blobInfo.filename());

              progress(30);

              const response = await fetch('/api/upload-image', {
                method: 'POST',
                body: formData,
              });

              progress(70);

              if (!response.ok) {
                const error = await response.json();
                reject(error.error || 'Upload failed');
                return;
              }

              const data = await response.json();
              progress(100);
              console.log("📸 Image uploaded to server:", data.url);
              resolve(data.url); // Return URL instead of base64
            } catch (error) {
              console.error("Upload error:", error);
              reject("Tải hình ảnh thất bại! Vui lòng thử lại.");
            }
          });
        },

        // Video upload handler - similar to image upload
        file_picker_callback: (callback, value, meta) => {
          if (meta.filetype === 'media') {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'video/*');

            input.onchange = function() {
              const file = this.files[0];
              if (file) {
                // Check file size (max 500MB for videos)
                if (file.size > 500 * 1024 * 1024) {
                  alert("Video quá lớn! Vui lòng chọn file nhỏ hơn 500MB.");
                  return;
                }

                // Check if it's a video file
                if (!file.type.startsWith('video/')) {
                  alert("Vui lòng chọn file video hợp lệ!");
                  return;
                }

                const reader = new FileReader();
                reader.onload = function(e) {
                  console.log("Video uploaded:", file.name);
                  callback(e.target.result, {
                    alt: file.name,
                    title: file.name
                  });
                };
                reader.readAsDataURL(file);
              }
            };

            input.click();
          }
        },

        // Enable drag & drop images
        images_reuse_filename: true,
        automatic_uploads: true, // Tự động upload khi paste/drag-drop hình

        // Media plugin configuration for video support
        media_live_embeds: true,
        media_filter_html: false,
        media_url_resolver: (data, resolve) => {
          // Handle video URLs and files
          if (data.url.startsWith('data:video/')) {
            resolve({
              html: `<video controls style="max-width: 100%;"><source src="${data.url}">Your browser does not support the video tag.</video>`
            });
          } else {
            resolve({html: ''});
          }
        },

        // Paste settings - let TinyMCE handle blob images via images_upload_handler
        paste_data_images: true,
        automatic_uploads: true, // Auto upload pasted images immediately
        images_reuse_filename: true,

        // Image settings for better base64 support with compression
        images_dataimg_filter: function(img) {
          return true; // Allow all base64 images
        },

        // Image upload handler - upload to server instead of base64 (TinyMCE 6+ format with Promise)
        images_upload_handler: (blobInfo) => {
          return new Promise(async (resolve, reject) => {
            try {
              let file = blobInfo.blob();

              // Compress if file is larger than 500KB
              if (file.size > 500000) {
                file = await new Promise((compressResolve) => {
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  const img = new Image();

                  img.onload = function() {
                    let { width, height } = img;
                    const maxSize = 800;

                    if (width > maxSize || height > maxSize) {
                      if (width > height) {
                        height = (height * maxSize) / width;
                        width = maxSize;
                      } else {
                        width = (width * maxSize) / height;
                        height = maxSize;
                      }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);
                    canvas.toBlob((blob) => compressResolve(blob), 'image/jpeg', 0.8);
                  };

                  img.src = URL.createObjectURL(file);
                });
              }

              // Upload to server
              const formData = new FormData();
              formData.append('file', file, blobInfo.filename() || 'image.jpg');

              const response = await fetch('/api/upload-image', {
                method: 'POST',
                body: formData,
              });

              if (!response.ok) {
                const error = await response.json();
                reject(error.error || 'Upload failed');
                return;
              }

              const data = await response.json();
              console.log("📸 Image uploaded to server:", data.url);
              
              // Fix: TinyMCE 6 throwing "r.indexOf is not a function" implies it treats the result as a string
              // resolving directly with URL string to fix this compatibility issue
              resolve(data.url);
            } catch (error) {
              console.error("Upload error:", error);
              reject("Tải hình ảnh thất bại!");
            }
          });
        },

        // Content filtering to preserve base64 images
        valid_elements: '*[*]',
        extended_valid_elements: 'img[*]',

        // Advanced settings
        convert_urls: false,
        remove_script_host: false,
        relative_urls: false,

        // WordPress-style color palette
        textcolor_map: [
          "000000",
          "Black",
          "993300",
          "Burnt orange",
          "333300",
          "Dark olive",
          "003300",
          "Dark green",
          "003366",
          "Dark azure",
          "000080",
          "Navy Blue",
          "333399",
          "Indigo",
          "333333",
          "Very dark gray",
          "800000",
          "Maroon",
          "FF6600",
          "Orange",
          "808000",
          "Olive",
          "008000",
          "Green",
          "008080",
          "Teal",
          "0000FF",
          "Blue",
          "666699",
          "Grayish blue",
          "808080",
          "Gray",
          "FF0000",
          "Red",
          "FF9900",
          "Amber",
          "99CC00",
          "Yellow green",
          "339966",
          "Sea green",
          "33CCCC",
          "Turquoise",
          "3366FF",
          "Royal blue",
          "800080",
          "Purple",
          "999999",
          "Medium gray",
          "FF00FF",
          "Magenta",
          "FFCC00",
          "Gold",
          "FFFF00",
          "Yellow",
          "00FF00",
          "Lime",
          "00FFFF",
          "Aqua",
          "00CCFF",
          "Sky blue",
          "993366",
          "Red violet",
          "FFFFFF",
          "White",
          "FF99CC",
          "Pink",
          "FFCC99",
          "Peach",
          "FFFF99",
          "Light yellow",
          "CCFFCC",
          "Pale green",
          "CCFFFF",
          "Pale cyan",
          "99CCFF",
          "Light sky blue",
          "CC99FF",
          "Plum",
        ],

        // Setup callback
        setup: (editor) => {
          editorInstanceRef.current = editor;

          // TinyMCE will automatically upload blob images via images_upload_handler
          // No need for manual image error handling - it causes conflicts with auto upload

          // Add custom font size button with current size detection
          editor.ui.registry.addMenuButton("customfontsizeselect", {
            text: "Font Size",
            tooltip: "Font sizes",
            onSetup: (api) => {
              // Update button text when cursor moves
              const updateButtonText = () => {
                const node = editor.selection.getNode();
                const fontSize =
                  editor.dom.getStyle(node, "font-size") ||
                  editor.dom.getAttrib(node, "size") ||
                  getComputedStyle(node).fontSize ||
                  "14px";

                // Clean up the font size value
                let cleanSize = fontSize;
                if (fontSize.includes("px")) {
                  cleanSize = fontSize;
                } else if (fontSize.includes("pt")) {
                  // Convert pt to px (approximate)
                  const ptValue = parseFloat(fontSize);
                  cleanSize = Math.round(ptValue * 1.33) + "px";
                } else if (!isNaN(fontSize)) {
                  cleanSize = fontSize + "px";
                }

                api.setText(cleanSize);
              };

              editor.on("NodeChange SelectionChange", updateButtonText);
              updateButtonText(); // Initial update

              return () => {
                editor.off("NodeChange SelectionChange", updateButtonText);
              };
            },
            fetch: (callback) => {
              const fontSizes = [
                "8px",
                "10px",
                "12px",
                "14px",
                "16px",
                "20px",
                "24px",
                "28px",
                "32px",
                "36px",
                "48px",
                "60px",
                "72px",
                "96px",
              ];
              const items = fontSizes.map((size) => ({
                type: "menuitem",
                text: size,
                onAction: () => {
                  // Simple approach - just wrap with style
                  const selectedText = editor.selection.getContent();
                  if (selectedText) {
                    // Has selection - wrap it
                    editor.selection.setContent(
                      `<span style="font-size: ${size};">${selectedText}</span>`
                    );
                  } else {
                    // No selection - insert span and place cursor inside
                    editor.insertContent(
                      `<span style="font-size: ${size};">Type here</span>`
                    );
                  }
                  console.log("Applied font size:", size);
                },
              }));
              callback(items);
            },
          });

          // Add custom image upload button with preview
          editor.ui.registry.addButton("customimageupload", {
            text: "🖼️",
            tooltip: "Upload Image with Preview",
            onAction: () => {
              // Create file input element
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "image/*";
              input.style.display = "none";

              input.onchange = (e) => {
                const file = e.target.files[0];
                if (file) {
                  // Validate image file
                  if (!file.type.startsWith("image/")) {
                    alert("Vui lòng chọn file hình ảnh hợp lệ!");
                    return;
                  }

                  // Check file size (max 5MB)
                  if (file.size > 5 * 1024 * 1024) {
                    alert("File quá lớn! Vui lòng chọn file nhỏ hơn 5MB.");
                    return;
                  }

                  const reader = new FileReader();
                  reader.onload = (event) => {
                    const imgSrc = event.target.result;

                    // Show preview dialog
                    editor.windowManager.open({
                      title: "Image Preview & Insert",
                      body: {
                        type: "panel",
                        items: [
                          {
                            type: "htmlpanel",
                            html: `
                              <div style="text-align: center; margin: 10px;">
                                <img src="${imgSrc}" style="max-width: 300px; max-height: 200px; border: none; border-radius: 4px;" />
                                <p style="margin: 10px 0; font-size: 12px; color: #666;">
                                  File: ${file.name}<br/>
                                  Size: ${(file.size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            `,
                          },
                          {
                            type: "input",
                            name: "alt",
                            label: "Alt text (mô tả hình ảnh)",
                            placeholder: "Nhập mô tả hình ảnh...",
                          },
                          {
                            type: "input",
                            name: "width",
                            label: "Width (px)",
                            placeholder: "auto",
                          },
                          {
                            type: "selectbox",
                            name: "align",
                            label: "Alignment",
                            items: [
                              { text: "None", value: "" },
                              { text: "Left", value: "left" },
                              { text: "Center", value: "center" },
                              { text: "Right", value: "right" },
                            ],
                          },
                        ],
                      },
                      buttons: [
                        {
                          type: "cancel",
                          text: "Cancel",
                        },
                        {
                          type: "submit",
                          text: "Insert Image",
                          primary: true,
                        },
                      ],
                      onSubmit: async (api) => {
                        const data = api.getData();

                        // ✅ Upload to server FIRST, then insert URL (not base64)
                        try {
                          api.block('Đang upload hình ảnh...');
                          
                          const formData = new FormData();
                          formData.append('file', file, file.name);

                          const response = await fetch('/api/upload-image', {
                            method: 'POST',
                            body: formData,
                          });

                          if (!response.ok) {
                            const error = await response.json();
                            throw new Error(error.error || 'Upload failed');
                          }

                          const uploadResult = await response.json();
                          const serverUrl = uploadResult.url;
                          console.log('📸 Image uploaded to server:', serverUrl);

                          // Build image HTML with server URL
                          let style = "";
                          let wrapperStyle = "";
                          if (data.align === "center") {
                            style = "display: block; margin: 0 auto; max-width: 100%; height: auto;";
                            wrapperStyle = "text-align: center;";
                          } else if (data.align === "left") {
                            style = "float: left; margin: 0 10px 10px 0; max-width: 100%; height: auto;";
                          } else if (data.align === "right") {
                            style = "float: right; margin: 0 0 10px 10px; max-width: 100%; height: auto;";
                          } else {
                            style = "max-width: 100%; height: auto;";
                          }

                          // Create image tag with SERVER URL
                          let imgTag = `<img src="${serverUrl}"`;
                          if (data.alt) imgTag += ` alt="${data.alt}"`;
                          if (data.width) imgTag += ` width="${data.width}"`;
                          if (style) imgTag += ` style="${style}"`;
                          imgTag += " />";

                          // Wrap center images in a paragraph for better alignment
                          let imgHtml;
                          if (data.align === "center") {
                            imgHtml = `<p style="${wrapperStyle}">${imgTag}</p>`;
                          } else {
                            imgHtml = imgTag;
                          }

                          api.unblock();
                          api.close();

                          // Insert image into editor
                          editor.insertContent(imgHtml);
                          editor.fire("change");

                          // Show success notification
                          editor.notificationManager.open({
                            text: `✅ Hình ảnh "${file.name}" đã được upload và thêm thành công!`,
                            type: "success",
                            timeout: 3000,
                          });

                          console.log("Image inserted:", file.name);
                        } catch (uploadError) {
                          api.unblock();
                          console.error('Upload error:', uploadError);
                          editor.notificationManager.open({
                            text: `❌ Lỗi upload: ${uploadError.message}`,
                            type: "error",
                            timeout: 5000,
                          });
                        }
                      },
                    });
                  };
                  reader.readAsDataURL(file);
                }

                // Clean up
                document.body.removeChild(input);
              };

              // Trigger file selection
              document.body.appendChild(input);
              input.click();
            },
          });

          // Add custom video upload button with preview
          editor.ui.registry.addButton("customvideoupload", {
            text: "🎥",
            tooltip: "Upload Video with Preview",
            onAction: () => {
              // Create file input element
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "video/*";
              input.style.display = "none";

              input.onchange = (e) => {
                const file = e.target.files[0];
                if (file) {
                  // Validate video file
                  if (!file.type.startsWith("video/")) {
                    alert("Vui lòng chọn file video hợp lệ!");
                    return;
                  }

                  // Check file size (max 500MB)
                  if (file.size > 500 * 1024 * 1024) {
                    alert("Video quá lớn! Vui lòng chọn file nhỏ hơn 500MB.");
                    return;
                  }

                  const reader = new FileReader();
                  reader.onload = (event) => {
                    const videoSrc = event.target.result;

                    // Show preview dialog
                    editor.windowManager.open({
                      title: "Video Preview & Insert",
                      body: {
                        type: "panel",
                        items: [
                          {
                            type: "htmlpanel",
                            html: `
                              <div style="text-align: center; margin: 10px;">
                                <video controls style="max-width: 300px; max-height: 200px; border: none; border-radius: 4px;">
                                  <source src="${videoSrc}" type="${file.type}">
                                  Your browser does not support the video tag.
                                </video>
                                <p style="margin: 10px 0; font-size: 12px; color: #666;">
                                  File: ${file.name}<br/>
                                  Size: ${(file.size / (1024 * 1024)).toFixed(1)} MB<br/>
                                  Type: ${file.type}
                                </p>
                              </div>
                            `,
                          },
                          {
                            type: "input",
                            name: "width",
                            label: "Width (px)",
                            placeholder: "auto",
                          },
                          {
                            type: "input",
                            name: "height",
                            label: "Height (px)",
                            placeholder: "auto",
                          },
                          {
                            type: "selectbox",
                            name: "align",
                            label: "Alignment",
                            items: [
                              { text: "None", value: "" },
                              { text: "Left", value: "left" },
                              { text: "Center", value: "center" },
                              { text: "Right", value: "right" },
                            ],
                          },
                          {
                            type: "checkbox",
                            name: "controls",
                            label: "Show controls",
                            checked: true,
                          },
                          {
                            type: "checkbox",
                            name: "autoplay",
                            label: "Autoplay",
                            checked: false,
                          },
                          {
                            type: "checkbox",
                            name: "loop",
                            label: "Loop",
                            checked: false,
                          },
                        ],
                      },
                      buttons: [
                        {
                          type: "cancel",
                          text: "Cancel",
                        },
                        {
                          type: "submit",
                          text: "Insert Video",
                          primary: true,
                        },
                      ],
                      onSubmit: (api) => {
                        const data = api.getData();

                        // Build video HTML with proper wrapper for center alignment
                        let style = "";
                        let wrapperStyle = "";
                        if (data.align === "center") {
                          style = "display: block; margin: 0 auto; max-width: 100%;";
                          wrapperStyle = "text-align: center;";
                        } else if (data.align === "left") {
                          style = "float: left; margin: 0 10px 10px 0; max-width: 100%;";
                        } else if (data.align === "right") {
                          style = "float: right; margin: 0 0 10px 10px; max-width: 100%;";
                        }

                        // Create video tag
                        let videoTag = `<video`;
                        if (data.width) videoTag += ` width="${data.width}"`;
                        if (data.height) videoTag += ` height="${data.height}"`;
                        if (style) videoTag += ` style="${style}"`;
                        if (data.controls) videoTag += ` controls`;
                        if (data.autoplay) videoTag += ` autoplay`;
                        if (data.loop) videoTag += ` loop`;
                        videoTag += `><source src="${videoSrc}" type="${file.type}">Your browser does not support the video tag.</video>`;

                        // Wrap center videos in a paragraph for better alignment
                        let videoHtml;
                        if (data.align === "center") {
                          videoHtml = `<p style="${wrapperStyle}">${videoTag}</p>`;
                        } else {
                          videoHtml = videoTag;
                        }

                        // Insert video into editor
                        editor.insertContent(videoHtml);

                        // Force refresh editor to display video
                        setTimeout(() => {
                          const currentContent = editor.getContent();
                          editor.setContent(currentContent);
                          editor.selection.setCursorLocation();
                          editor.nodeChanged();
                          editor.fire("change");
                        }, 500);

                        api.close();

                        // Show success notification
                        editor.notificationManager.open({
                          text: `✅ Video "${file.name}" đã được thêm thành công!`,
                          type: "success",
                          timeout: 3000,
                        });

                        console.log("Video inserted:", file.name);
                      },
                    });
                  };
                  reader.readAsDataURL(file);
                }

                // Clean up
                document.body.removeChild(input);
              };

              // Trigger file selection
              document.body.appendChild(input);
              input.click();
            },
          });

          // WordPress Advanced Editor Tools events
          editor.on("init", () => {
            editor.setContent(valueRef.current || "");
            console.log(
              "WordPress Advanced Editor Tools initialized with custom font size selector"
            );

            // Register font size formats - simplified approach
            try {
              if (fontSizeHelperRef.current && editor.formatter) {
                fontSizeHelperRef.current.registerFontSizeFormats(editor);
              }
            } catch (err) {
              console.log(
                "FontSize registration error (safe to ignore):",
                err.message
              );
            }

            // Debug: Safe check for controls
            try {
              console.log(
                "Available UI components:",
                Object.keys(editor.ui.registry.getAll())
              );
              console.log(
                "Fontsize formats:",
                editor.settings.fontsize_formats
              );
            } catch (err) {
              console.log("Debug error (safe to ignore):", err.message);
            }

            // Update word count on init
            if (wordCounterRef.current) {
              const count = wordCounterRef.current.count(content);
              setWordCount(count);
            }
          });

          editor.on("change input undo redo", () => {
            const newContent = editor.getContent();
            setContent(newContent);
            onChange(newContent);
            setIsDirty(true);

            // Update word count
            if (wordCounterRef.current) {
              const count = wordCounterRef.current.count(newContent);
              setWordCount(count);
            }

            // Schedule auto-save
            if (autoSaveRef.current) {
              autoSaveRef.current.schedule("editor_content", newContent);
            }
          });

          // WordPress-style paste handling
          editor.on("paste", (e) => {
            // Handle image paste
            const items = (e.clipboardData || e.originalEvent.clipboardData)
              .items;
            for (let item of items) {
              if (item.type.indexOf("image") === 0) {
                e.preventDefault();
                const file = item.getAsFile();
                if (imageUploaderRef.current) {
                  imageUploaderRef.current
                    .process(file)
                    .then((dataUrl) => {
                      editor.insertContent(
                        `<img src="${dataUrl}" alt="Pasted image" />`
                      );
                    })
                    .catch((err) => {
                      console.error("Error processing pasted image:", err);
                    });
                }
                break;
              }
            }
          });

          // Keyboard shortcuts giống WordPress
          editor.addShortcut("Meta+S", "Save content", () => {
            // Save immediately
            if (autoSaveRef.current) {
              autoSaveRef.current.save("editor_content", editor.getContent());
            }
            setIsDirty(false);

            // Trigger save event
            const event = new CustomEvent("editor-save");
            document.dispatchEvent(event);
          });

          editor.addShortcut("Ctrl+S", "Save content", () => {
            // Save immediately
            if (autoSaveRef.current) {
              autoSaveRef.current.save("editor_content", editor.getContent());
            }
            setIsDirty(false);

            // Trigger save event
            const event = new CustomEvent("editor-save");
            document.dispatchEvent(event);
          });

          editor.addShortcut("Meta+B", "Bold", () => {
            editor.execCommand("Bold");
          });

          editor.addShortcut("Ctrl+B", "Bold", () => {
            editor.execCommand("Bold");
          });

          editor.addShortcut("Meta+I", "Italic", () => {
            editor.execCommand("Italic");
          });

          editor.addShortcut("Ctrl+I", "Italic", () => {
            editor.execCommand("Italic");
          });

          editor.addShortcut("Meta+U", "Underline", () => {
            editor.execCommand("Underline");
          });

          editor.addShortcut("Ctrl+U", "Underline", () => {
            editor.execCommand("Underline");
          });

          // WordPress-style link shortcuts
          editor.addShortcut("Meta+K", "Insert Link", () => {
            const selection = editor.selection.getContent({ format: "text" });
            const url = prompt("Nhập URL:", "https://");
            if (url) {
              if (selection) {
                editor.insertContent(
                  `<a href="${url}" target="_blank" rel="noopener noreferrer">${selection}</a>`
                );
              } else {
                editor.insertContent(
                  `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
                );
              }
            }
          });

          editor.addShortcut("Ctrl+K", "Insert Link", () => {
            const selection = editor.selection.getContent({ format: "text" });
            const url = prompt("Nhập URL:", "https://");
            if (url) {
              if (selection) {
                editor.insertContent(
                  `<a href="${url}" target="_blank" rel="noopener noreferrer">${selection}</a>`
                );
              } else {
                editor.insertContent(
                  `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
                );
              }
            }
          });
        },

        // Mobile responsive
        mobile: {
          theme: "mobile",
          plugins: ["autosave", "lists", "autolink"],
          toolbar: ["undo", "bold", "italic", "styleselect"],
        },
      });
    }

    return () => {
      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy();
        editorInstanceRef.current = null;
      }
    };
  }, [isLoaded]);

  // Update content when props change
  useEffect(() => {
    valueRef.current = value;
    if (value !== content) {
      setContent(value || "");
      if (editorInstanceRef.current) {
        editorInstanceRef.current.setContent(value || "");
      }
    }
  }, [value]);

  if (!isLoaded) {
    return (
      <div className="classic-editor-loading">
        <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg border">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">
              Đang tải WordPress Classic Editor...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wp-classic-editor-advanced">
      {/* TinyMCE Editor - Dùng nút image trong toolbar để upload hình (tự động upload lên server) */}
      <div
        ref={containerRef}
        className="classic-editor-wrapper tadvdropzone"
      ></div>

      {/* WordPress Advanced Editor Tools Info Bar */}
      <div className="wp-editor-advanced-info">
        <div className="info-left">
          <span>🔥 WordPress Advanced Editor Tools</span>
          <span>
            📄 <strong>Format Dropdown</strong>: Paragraph/Headings
          </span>
          <span>
            📏 <strong>Font Size Dropdown</strong>: 8px-96px (Custom)
          </span>
          <span>
            🎨 <strong>Font Family</strong> + Colors
          </span>
          <span>💾 Ctrl+S để lưu</span>
          <span>🖼️ Drag & Drop images</span>
          {isDirty && <span className="text-orange-600">● Chưa lưu</span>}
        </div>
        <div className="info-right">
          <div className="word-count">
            Từ: <span className="font-size-indicator">{wordCount}</span>
          </div>
          <div className="auto-save-status">
            {autoSaveRef.current && (
              <span className="format-indicator">✓ Auto-save: 30s</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Utility function to check localStorage quota and data size
const checkLocalStorageQuota = (key, data) => {
  try {
    const jsonString = JSON.stringify(data);
    const sizeInBytes = new Blob([jsonString]).size;
    const sizeInMB = sizeInBytes / (1024 * 1024);

    console.log(`📊 Data size for ${key}: ${sizeInMB.toFixed(2)} MB`);

    // Check if data is too large (> 8MB to be safe - tăng từ 4MB)
    if (sizeInMB > 8) {
      console.warn(`⚠️ Data too large for localStorage: ${sizeInMB.toFixed(2)} MB`);
      // Tự động dọn dẹp dữ liệu cũ
      try {
        localStorage.removeItem('old_questions');
        localStorage.removeItem('backup_data');
        localStorage.removeItem('temp_data');
        localStorage.removeItem('cached_data');
        console.log("🧹 Cleaned up old localStorage data");
      } catch (cleanupError) {
        console.warn("⚠️ Could not clean up localStorage:", cleanupError);
      }
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error checking data size:", error);
    return false;
  }
};

const QuestionManager = () => {
  const navigate = useNavigate();
  const editorRef = useRef(null);
  // Authentication handled by SecureAuth wrapper - removed old auth states

  // Get saved tab from localStorage or default to dashboard
  const savedTab = localStorage.getItem("admin_current_tab") || "dashboard";
  const savedTestTab = localStorage.getItem("admin_current_test_tab") || "mbti";
  const savedSelectedTest = localStorage.getItem("admin_selected_test") || "mbti";
  const savedSelectedTemplateId = localStorage.getItem("admin_selected_template") || "default";

  const [currentTab, setCurrentTab] = useState(savedTab);
  const [currentTestTab, setCurrentTestTab] = useState(savedTestTab);
  const [mbtiQuestions, setMbtiQuestions] = useState([]);
  const [iqQuestions, setIqQuestions] = useState([]);
  const [eqQuestions, setEqQuestions] = useState([]);
  const [discQuestions, setDiscQuestions] = useState([]);
  const [hollandQuestions, setHollandQuestions] = useState([]);
  const [customContent, setCustomContent] = useState({});
  const [currentVerificationCount, setCurrentVerificationCount] = useState(0);
  const [totalVerificationCount, setTotalVerificationCount] = useState(0);
  const [dailySwitchCount, setDailySwitchCount] = useState(0);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogTotal, setActivityLogTotal] = useState(0);
  const [activityLogPage, setActivityLogPage] = useState(0);
  const [loadingActivityLog, setLoadingActivityLog] = useState(false);
  const [activityLogDateFilter, setActivityLogDateFilter] = useState('all'); // 'all', 'today', 'yesterday', 'last7days', 'custom'
  const [activityLogCustomDate, setActivityLogCustomDate] = useState('');
  // Template support for custom content (MBTI)
  const [selectedTemplateId, setSelectedTemplateId] = useState(savedSelectedTemplateId);
  const [activeTemplateId, setActiveTemplateId] = useState("default");
  const [settingActiveLoading, setSettingActiveLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  const [selectedTest, setSelectedTest] = useState(savedSelectedTest);
  const [showAddQuestionForm, setShowAddQuestionForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    question: "",
    options: [
      {
        text: "",
        value: "",
        option_text: "",
        option_value: "",
        correct_answer: 0,
      },
      {
        text: "",
        value: "",
        option_text: "",
        option_value: "",
        correct_answer: 0,
      },
    ],
  });

  // Notification system
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success", // success, error, warning, info
  });

  // Force refresh key for MarkdownRenderer
  const [refreshKey, setRefreshKey] = useState(0);

  // Function to reset newQuestion with correct number of options
  const resetNewQuestion = () => {
    if (["mbti", "iq", "disc"].includes(currentTestTab)) {
      const numOptions = currentTestTab === "mbti" ? 2 : 4;
      console.log("reset options to", numOptions, "for tab", currentTestTab); // Debug log
      const options = Array.from({ length: numOptions }, () => ({
        text: "",
        value: "",
        option_text: "",
        option_value: "",
        correct_answer: 0,
      }));

      setNewQuestion({
        question: "",
        options: options,
      });
    }
  };

  // Update newQuestion options when currentTestTab changes OR form opens
  useEffect(() => {
    resetNewQuestion(); // Luôn reset khi có thay đổi
  }, [showAddQuestionForm, currentTestTab]);

  // Function to toggle add form with direct reset
  const openAddForm = () => {
    if (!showAddQuestionForm) {
      resetNewQuestion(); // Reset ngay khi chuẩn bị mở form
    }
    setShowAddQuestionForm(!showAddQuestionForm);
  };

  // Settings states
  const [testPasswords, setTestPasswords] = useState({
    mbti: "mbti2025",
    iq: "iq2025",
    eq: "eq2025",
    disc: "disc2025",
    holland: "holland2025",
  });
  const [headerFooterSettings, setHeaderFooterSettings] = useState({
    head_code: "",
    body_code: "",
    footer_code: "",
    enable_head: true,
    enable_body: true,
    enable_footer: true,
  });

  // Content Management states - moved to component level
  const [tempContent, setTempContent] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Password change states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Login attempts tracking - removed (handled by SecureAuth)

  // SEO Management states
  const [seoSettings, setSeoSettings] = useState({
    home: {
      h1: "Test IQ - Kiểm tra chỉ số thông minh trực tuyến",
      metaDescription:
        "Làm bài test IQ online miễn phí, kiểm tra chỉ số thông minh EQ, DISC, MBTI và Holland Code. Kết quả chi tiết và chính xác nhất.",
      title: "Test IQ Online - Kiểm tra chỉ số thông minh miễn phí",
    },
    about: {
      h1: "Giới thiệu về các bài test tâm lý và trí tuệ",
      metaDescription:
        "Tìm hiểu về các loại test tâm lý: IQ, EQ, MBTI, DISC và Holland Code. Ý nghĩa và cách áp dụng trong cuộc sống.",
      title: "Giới thiệu - Test IQ và các bài test tâm lý",
    },
    mbti: {
      h1: "Test MBTI - Kiểm tra tính cách 16 nhóm",
      metaDescription:
        "Làm bài test MBTI miễn phí để khám phá tính cách của bạn thuộc 1 trong 16 nhóm tính cách. Kết quả chi tiết và chính xác.",
      title: "Test MBTI Online - Kiểm tra tính cách 16 nhóm",
    },
    iq: {
      h1: "Test IQ Online - Kiểm tra chỉ số thông minh",
      metaDescription:
        "Làm bài test IQ online với 25 câu hỏi chuẩn quốc tế. Đo lường chỉ số thông minh IQ của bạn một cách chính xác.",
      title: "Test IQ Online - Kiểm tra chỉ số thông minh miễn phí",
    },
    eq: {
      h1: "Test EQ - Kiểm tra trí tuệ cảm xúc",
      metaDescription:
        "Đánh giá trí tuệ cảm xúc EQ qua bài test với 13 câu hỏi. Hiểu rõ khả năng quản lý cảm xúc và giao tiếp của bạn.",
      title: "Test EQ Online - Kiểm tra trí tuệ cảm xúc",
    },
    disc: {
      h1: "Test DISC - Phân tích phong cách hành vi",
      metaDescription:
        "Khám phá phong cách hành vi DISC của bạn qua 24 câu hỏi. Hiểu rõ cách làm việc và giao tiếp hiệu quả.",
      title: "Test DISC Online - Phân tích phong cách hành vi",
    },
    holland: {
      h1: "Holland Code - Tìm kiếm nghề nghiệp phù hợp",
      metaDescription:
        "Khám phá sở thích nghề nghiệp qua test Holland Code với 12 hoạt động. Tìm ra con đường sự nghiệp phù hợp nhất.",
      title: "Test Holland Code - Tìm kiếm nghề nghiệp phù hợp",
    },
  });

  // Page editing state for SEO management
  const [editingPage, setEditingPage] = useState(null);

  // Loading state for async operations
  const [loading, setLoading] = useState(false);

  // MAX_LOGIN_ATTEMPTS removed - handled by SecureAuth

  // Show notification function
  const showNotification = (message, type = "success") => {
    setNotification({
      show: true,
      message,
      type,
    });

    // Auto hide after 3 seconds
    setTimeout(() => {
      setNotification((prev) => ({ ...prev, show: false }));
    }, 3000);
  };

  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "questions", label: "Quản lý câu hỏi" },
    { id: "pages", label: "Quản lý trang" },
    { id: "content", label: "Nội dung tùy chỉnh" },
    { id: "statistics", label: "Thống kê" },
    { id: "settings", label: "Cài đặt" },
  ];

  const testTabs = [
    {
      id: "mbti",
      label: "Test MBTI",
      icon: "🧠",
      color: "from-blue-500 to-purple-500",
    },
    {
      id: "iq",
      label: "Test IQ",
      icon: "🤔",
      color: "from-green-500 to-teal-500",
    },
    {
      id: "iq_answers",
      label: "Đáp án Test IQ",
      icon: "📝",
      color: "from-cyan-500 to-blue-500",
    },
    {
      id: "eq",
      label: "Test EQ",
      icon: "❤️",
      color: "from-pink-500 to-rose-500",
    },
    {
      id: "disc",
      label: "Test DISC",
      icon: "🎯",
      color: "from-orange-500 to-red-500",
    },
    {
      id: "holland",
      label: "Holland Codes",
      icon: "🌟",
      color: "from-yellow-500 to-orange-500",
    },
  ];

  // Tests that support multi-templates in Custom Content
  const templatableTests = ["mbti", "iq", "eq", "disc", "holland"]; // exclude 'iq_answers'

  // Single effect to manage content and template synchronization
  useEffect(() => {
    const section = customContent?.[selectedTest] || {};

    if (section.templates && typeof section.templates === "object") {
      const ids = Object.keys(section.templates);
      const activeId = section.activeTemplateId || ids[0] || "default";
      
      // 1. Validate/Update selectedTemplateId if it's invalid or missing
      const nextSelected = ids.includes(selectedTemplateId)
        ? selectedTemplateId
        : activeId;

      if (selectedTemplateId !== nextSelected) {
        setSelectedTemplateId(nextSelected);
        localStorage.setItem("admin_selected_template", nextSelected);
        return; // Wait for next effect run with updated selectedTemplateId
      }
      
      setActiveTemplateId(activeId);

      // 2. Set tempContent from the validated selectedTemplateId
      const content = section.templates?.[selectedTemplateId]?.instructions || "";
      setTempContent(content);
    } else {
      // Legacy or non-template mode
      if (selectedTemplateId !== "default") {
        setSelectedTemplateId("default");
        localStorage.setItem("admin_selected_template", "default");
      }
      setActiveTemplateId("default");
      setTempContent(section?.instructions || "");
    }
    setHasUnsavedChanges(false);
  }, [selectedTest, customContent, selectedTemplateId]);

  // Load current verification count for selected test (auto-rotate display)
  useEffect(() => {
    const loadCurrentCount = async () => {
      try {
        const templatableTests = ["mbti", "iq", "eq", "disc", "holland"]; // same as defined above
        if (!templatableTests.includes(selectedTest)) return;
        const data = await autoRotateAPI.getCurrentCount(selectedTest);
        if (data && data.success) {
          setCurrentVerificationCount(Number(data.count || 0));
          setTotalVerificationCount(Number(data.totalCount || 0));
          setDailySwitchCount(Number(data.dailySwitchCount || 0));

          // ✅ FIX: Always sync active template with server (not just when different)
          // This ensures Admin Panel shows correct template after daily reset
          const serverActiveId = data.activeTemplateId || null;

          // Always update if server has a valid activeTemplateId
          if (serverActiveId) {
            setCustomContent(prevContent => {
              const section = prevContent?.[selectedTest] || {};

              // Only update if different OR if section doesn't have activeTemplateId yet
              if (!section.activeTemplateId || serverActiveId !== section.activeTemplateId) {
                console.log(`🔄 [${selectedTest}] Syncing active template: "${section.activeTemplateId || 'none'}" → "${serverActiveId}"`);
                const updatedSection = { ...section, activeTemplateId: serverActiveId };
                const updatedContent = { ...prevContent, [selectedTest]: updatedSection };

                try {
                  localStorage.setItem('custom_content', JSON.stringify(updatedContent));
                  console.log(`✅ [${selectedTest}] Active template synced to localStorage`);
                } catch (e) {
                  console.warn(`⚠️ [${selectedTest}] Failed to sync to localStorage:`, e);
                }

                return updatedContent;
              }

              return prevContent;
            });
          }
        }
      } catch (e) {
        console.warn('⚠️ Failed to load current verification count:', e);
      }
    };
    loadCurrentCount();
  }, [selectedTest, selectedTemplateId]);

  // Load activity log
  const loadActivityLog = async (page = 0) => {
    try {
      setLoadingActivityLog(true);
      const limit = 20;
      const offset = page * limit;

      // Build query params with date filter
      let url = `/api/custom-content/template-activity-log/${selectedTest}?limit=${limit}&offset=${offset}`;

      if (activityLogDateFilter !== 'all') {
        // Get current time in UTC+7
        const nowUTC = new Date();
        const nowUTC7 = new Date(nowUTC.getTime() + 7 * 60 * 60 * 1000);

        let startDate, endDate;

        if (activityLogDateFilter === 'today') {
          // Today in UTC+7: 00:00:00 to 23:59:59
          const year = nowUTC7.getUTCFullYear();
          const month = nowUTC7.getUTCMonth();
          const day = nowUTC7.getUTCDate();

          startDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
          endDate = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
        } else if (activityLogDateFilter === 'yesterday') {
          // Yesterday in UTC+7
          const yesterdayUTC7 = new Date(nowUTC7.getTime() - 24 * 60 * 60 * 1000);
          const year = yesterdayUTC7.getUTCFullYear();
          const month = yesterdayUTC7.getUTCMonth();
          const day = yesterdayUTC7.getUTCDate();

          startDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
          endDate = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
        } else if (activityLogDateFilter === 'last7days') {
          // Last 7 days in UTC+7
          const sevenDaysAgoUTC7 = new Date(nowUTC7.getTime() - 7 * 24 * 60 * 60 * 1000);
          const startYear = sevenDaysAgoUTC7.getUTCFullYear();
          const startMonth = sevenDaysAgoUTC7.getUTCMonth();
          const startDay = sevenDaysAgoUTC7.getUTCDate();

          const endYear = nowUTC7.getUTCFullYear();
          const endMonth = nowUTC7.getUTCMonth();
          const endDay = nowUTC7.getUTCDate();

          startDate = new Date(Date.UTC(startYear, startMonth, startDay, 0, 0, 0, 0));
          endDate = new Date(Date.UTC(endYear, endMonth, endDay, 23, 59, 59, 999));
        } else if (activityLogDateFilter === 'custom' && activityLogCustomDate) {
          // Custom date: Parse YYYY-MM-DD và tạo UTC+7 date
          const [year, month, day] = activityLogCustomDate.split('-').map(Number);

          // Tạo date ở UTC+7 timezone (00:00:00 to 23:59:59)
          startDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
          endDate = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
        }

        if (startDate && endDate) {
          // Convert UTC+7 dates back to UTC for backend
          const startDateUTC = new Date(startDate.getTime() - 7 * 60 * 60 * 1000);
          const endDateUTC = new Date(endDate.getTime() - 7 * 60 * 60 * 1000);

          url += `&startDate=${startDateUTC.toISOString()}&endDate=${endDateUTC.toISOString()}`;
        }
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setActivityLogs(data.logs || []);
        setActivityLogTotal(data.total || 0);
        setActivityLogPage(page);
      }
    } catch (error) {
      console.error('⚠️ Failed to load activity log:', error);
    } finally {
      setLoadingActivityLog(false);
    }
  };

  // Load activity log when opening modal or changing date filter
  useEffect(() => {
    if (showActivityLog) {
      loadActivityLog(0);
    }
  }, [showActivityLog, selectedTest, activityLogDateFilter, activityLogCustomDate]);

  // Load data from API and check admin session
  useEffect(() => {
    const loadDataFromAPI = async () => {
      try {
        console.log("🔄 Loading questions from database...");

        // Load all test types from API
        const testTypes = ["mbti", "iq", "eq", "disc", "holland"];
        const loadPromises = testTypes.map(async (testType) => {
          try {
            const questions = await questionsAPI.getByType(testType);
            console.log(
              `✅ Loaded ${
                questions.length
              } ${testType.toUpperCase()} questions from database`
            );
            return { testType, questions };
          } catch (error) {
            console.error(`❌ Failed to load ${testType} questions:`, error);
            return { testType, questions: [] };
          }
        });

        const results = await Promise.all(loadPromises);

        // Set questions from API
        results.forEach(({ testType, questions }) => {
          switch (testType) {
            case "mbti":
              setMbtiQuestions(questions);
              // Cache in localStorage as backup
              localStorage.setItem("mbti_questions", JSON.stringify(questions));
              break;
            case "iq":
              setIqQuestions(questions);
              localStorage.setItem("iq_questions", JSON.stringify(questions));
              break;
            case "eq":
              setEqQuestions(questions);
              localStorage.setItem("eq_questions", JSON.stringify(questions));
              break;
            case "disc":
              setDiscQuestions(questions);
              localStorage.setItem("disc_questions", JSON.stringify(questions));
              break;
            case "holland":
              setHollandQuestions(questions);
              localStorage.setItem(
                "holland_questions",
                JSON.stringify(questions)
              );
              break;
          }
        });

        showNotification("🎉 Đã tải tất cả câu hỏi từ database!", "success");
      } catch (error) {
        console.error(
          "❌ Failed to load data from API, falling back to localStorage:",
          error
        );
        showNotification(
          "⚠️ Không thể kết nối database, sử dụng dữ liệu local",
          "warning"
        );

        // Fallback to localStorage if API fails
        const savedMbtiQuestions = localStorage.getItem("mbti_questions");
        const savedIqQuestions = localStorage.getItem("iq_questions");
        const savedEqQuestions = localStorage.getItem("eq_questions");
        const savedDiscQuestions = localStorage.getItem("disc_questions");
        const savedHollandQuestions = localStorage.getItem("holland_questions");

        if (savedMbtiQuestions)
          setMbtiQuestions(JSON.parse(savedMbtiQuestions));
        if (savedIqQuestions) setIqQuestions(JSON.parse(savedIqQuestions));
        if (savedEqQuestions) setEqQuestions(JSON.parse(savedEqQuestions));
        if (savedDiscQuestions)
          setDiscQuestions(JSON.parse(savedDiscQuestions));
        if (savedHollandQuestions)
          setHollandQuestions(JSON.parse(savedHollandQuestions));
      }
    };

    // Load other settings from localStorage with error handling
    try {
      const savedCustomContent = localStorage.getItem("custom_content");
      const savedTestPasswords = localStorage.getItem("test_passwords");
      const savedHeaderFooterSettings = localStorage.getItem(
        "header_footer_settings"
      );
      // adminSession removed - handled by SecureAuth wrapper

      if (savedCustomContent) setCustomContent(JSON.parse(savedCustomContent));
      if (savedTestPasswords) setTestPasswords(JSON.parse(savedTestPasswords));
      if (savedHeaderFooterSettings)
        setHeaderFooterSettings(JSON.parse(savedHeaderFooterSettings));
    } catch (error) {
      console.warn("⚠️ Error loading from localStorage:", error);
      // Continue without localStorage data - will load from API
    }

    // Authentication checks removed - handled by SecureAuth wrapper

    // Load SEO settings
    const savedSeoSettings = localStorage.getItem("seo_settings");
    if (savedSeoSettings) {
      setSeoSettings(JSON.parse(savedSeoSettings));
    }

    // Fetch the latest SEO settings from the API and cache them
    (async () => {
      try {
        const apiSeoSettings = await seoAPI.getSettings();
        if (apiSeoSettings) {
          setSeoSettings(apiSeoSettings);
          localStorage.setItem("seo_settings", JSON.stringify(apiSeoSettings));
          console.log(
            "✅ Fetched SEO settings from API and updated localStorage"
          );
        }
      } catch (error) {
        console.error("❌ Failed to fetch SEO settings from API:", error);
      }
    })();

    // Load custom content from API
    (async () => {
      try {
        const apiCustomContent = await customContentAPI.get();
        if (apiCustomContent && Object.keys(apiCustomContent).length > 0) {
          setCustomContent(apiCustomContent);

          // Check data size before saving to localStorage
          if (checkLocalStorageQuota("custom_content", apiCustomContent)) {
            try {
              localStorage.setItem(
                "custom_content",
                JSON.stringify(apiCustomContent)
              );
              console.log(
                "✅ Fetched custom content from API and updated localStorage"
              );
            } catch (storageError) {
              if (storageError.name === 'QuotaExceededError' || storageError.code === 22) {
                console.warn("⚠️ localStorage quota exceeded when loading from API");
                // Clear old localStorage data to free up space
                localStorage.removeItem("custom_content");
              } else {
                console.error("❌ Error saving to localStorage:", storageError);
              }
            }
          } else {
            console.warn("⚠️ API content too large for localStorage");
            localStorage.removeItem("custom_content"); // Clear any existing data
          }
        }
      } catch (error) {
        console.error("❌ Failed to fetch custom content from API:", error);
      }
    })();

    // Load test passwords from API
    (async () => {
      try {
        const apiTestPasswords = await testPasswordsAPI.get();
        if (apiTestPasswords) {
          setTestPasswords(apiTestPasswords);
          localStorage.setItem(
            "test_passwords",
            JSON.stringify(apiTestPasswords)
          );
          console.log(
            "✅ Fetched test passwords from API and updated localStorage"
          );
        }
      } catch (error) {
        console.error("❌ Failed to fetch test passwords from API:", error);
      }
    })();

    // Load header/footer settings from API
    (async () => {
      try {
        const apiHeaderFooterSettings = await headerFooterAPI.get();
        if (apiHeaderFooterSettings) {
          setHeaderFooterSettings(apiHeaderFooterSettings);
          localStorage.setItem(
            "header_footer_settings",
            JSON.stringify(apiHeaderFooterSettings)
          );
          console.log(
            "✅ Fetched header/footer settings from API and updated localStorage"
          );
        }
      } catch (error) {
        console.error(
          "❌ Failed to fetch header/footer settings from API:",
          error
        );
      }
    })();

    // Load questions from API
    loadDataFromAPI();
  }, []);

  const getCurrentQuestions = () => {
    switch (currentTestTab) {
      case "mbti":
        return mbtiQuestions;
      case "iq":
        return iqQuestions;
      case "eq":
        return eqQuestions;
      case "disc":
        return discQuestions;
      case "holland":
        return hollandQuestions;
      default:
        return [];
    }
  };

  const setCurrentQuestions = (questions) => {
    switch (currentTestTab) {
      case "mbti":
        setMbtiQuestions(questions);
        localStorage.setItem("mbti_questions", JSON.stringify(questions));
        break;
      case "iq":
        setIqQuestions(questions);
        localStorage.setItem("iq_questions", JSON.stringify(questions));
        break;
      case "eq":
        setEqQuestions(questions);
        localStorage.setItem("eq_questions", JSON.stringify(questions));
        break;
      case "disc":
        setDiscQuestions(questions);
        localStorage.setItem("disc_questions", JSON.stringify(questions));
        break;
      case "holland":
        setHollandQuestions(questions);
        localStorage.setItem("holland_questions", JSON.stringify(questions));
        break;
    }
  };

  const saveCustomContent = async (content, options = {}) => {
    const { reload = true, activityType = null } = options;
    try {
      const response = await customContentAPI.update(content, activityType);
      const savedContent =
        response?.customContent && Object.keys(response.customContent).length > 0
          ? response.customContent
          : content;

      setCustomContent(savedContent);

      // Check data size before saving to localStorage
      if (checkLocalStorageQuota("custom_content", savedContent)) {
        try {
          localStorage.setItem("custom_content", JSON.stringify(savedContent));
          console.log("✅ Custom content saved to database and localStorage");
          if (reload) {
            showNotification("✅ Đã lưu thành công! Đang tải lại trang...", "success");
            // Auto reload after 1.5 seconds to show updated content
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          } else {
            showNotification("✅ Đã lưu cài đặt!", "success");
          }
        } catch (storageError) {
          if (storageError.name === 'QuotaExceededError' || storageError.code === 22) {
            console.warn("⚠️ localStorage quota exceeded, content saved to database only");
            // Clear old localStorage data to free up space
            localStorage.removeItem("custom_content");
            // Show success and reload anyway (data saved to database)
            if (reload) {
              showNotification("✅ Đã lưu vào database! Đang tải lại trang...", "success");
              setTimeout(() => {
                window.location.reload();
              }, 1500);
            } else {
              showNotification("✅ Đã lưu vào database!", "success");
            }
          } else {
            throw storageError;
          }
        }
      } else {
        // Data too large, don't save to localStorage
        console.warn("⚠️ Content too large for localStorage, saved to database only");
        localStorage.removeItem("custom_content"); // Clear any existing data
        // Show success and reload (data saved to database successfully)
        if (reload) {
          showNotification("✅ Đã lưu vào database! Đang tải lại trang...", "success");
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          showNotification("✅ Đã lưu vào database!", "success");
        }
      }

    } catch (error) {
      console.error("❌ Failed to save custom content:", error);
      showNotification("❌ Lỗi khi lưu nội dung: " + error.message, "error");
    }
  };

  // Optimized function to save only auto-rotate config (few KB instead of 30MB)
  const saveAutoRotateConfig = async (testType, templateId, templateData) => {
    try {
      await templateAPI.update(testType, templateId, templateData);
      console.log(`✅ Auto-rotate config saved for ${testType}/${templateId}`);
      showNotification("✅ Đã lưu cấu hình tự động đổi mẫu!", "success");
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("❌ Failed to save auto-rotate config:", error);
      showNotification("❌ Lỗi khi lưu: " + error.message, "error");
      throw error;
    }
  };

  // handleLogin function removed - now handled by Login.js component and SecureAuth wrapper

  const handleLogout = () => {
    setCurrentTab("dashboard");
    // Remove JWT token and session
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/");
  };

  // Change admin password function
  const changeAdminPassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showNotification("❌ Vui lòng điền đầy đủ thông tin!", "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      showNotification(
        "❌ Mật khẩu mới và xác nhận mật khẩu không khớp!",
        "error"
      );
      return;
    }

    if (newPassword.length < 6) {
      showNotification("❌ Mật khẩu mới phải có ít nhất 6 ký tự!", "error");
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");

      if (!token) {
        showNotification("❌ Không tìm thấy token xác thực!", "error");
        return;
      }

      const response = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showNotification("🔐 Đổi mật khẩu thành công!", "success");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        showNotification(
          `❌ ${data.error || "Có lỗi xảy ra khi đổi mật khẩu"}`,
          "error"
        );
      }
    } catch (error) {
      console.error("Change password error:", error);
      showNotification("❌ Lỗi kết nối khi đổi mật khẩu!", "error");
    }
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };



  // Question CRUD functions
  const addQuestion = async () => {
    let isValid = false;

    if (currentTestTab === "eq") {
      isValid = newQuestion.statement?.trim() && newQuestion.type?.trim();
    } else if (currentTestTab === "holland") {
      isValid = newQuestion.activity?.trim() && newQuestion.type?.trim();
    } else {
      // Check for both text and option_text formats
      let hasValidOptions = newQuestion.question?.trim();

      if (currentTestTab === "mbti") {
        // MBTI: Chỉ cần 2 options đầu tiên có data
        hasValidOptions =
          hasValidOptions &&
          newQuestion.options
            ?.slice(0, 2)
            .every(
              (opt) =>
                (opt.text?.trim() || opt.option_text?.trim()) &&
                (opt.value?.trim() || opt.option_value?.trim())
            );
      } else {
        // IQ/DISC: Cần tất cả options có data
        hasValidOptions =
          hasValidOptions &&
          newQuestion.options?.every(
            (opt) =>
              (opt.text?.trim() || opt.option_text?.trim()) &&
              (opt.value?.trim() || opt.option_value?.trim())
          );
      }

      // For IQ test, also check that exactly one correct answer is selected
      if (currentTestTab === "iq") {
        const correctAnswers =
          newQuestion.options?.filter((opt) => opt.correct_answer === 1) || [];
        isValid = hasValidOptions && correctAnswers.length === 1;
      } else {
        isValid = hasValidOptions;
      }
    }

    if (isValid) {
      // Set loading state
      setLoading(true);

      try {
        // Prepare question data for API
        const questionToAdd = {
          ...newQuestion,
          test_type: currentTestTab,
        };

        // Map options to API format
        if (questionToAdd.options) {
          // MBTI: Chỉ lưu 2 options đầu tiên
          const optionsToSave =
            currentTestTab === "mbti"
              ? questionToAdd.options.slice(0, 2)
              : questionToAdd.options;

          questionToAdd.options = optionsToSave.map((opt) => ({
            option_text: opt.option_text || opt.text || "",
            option_value: opt.option_value || opt.value || "",
            is_correct: opt.correct_answer || 0,
          }));
        }

        console.log("🔄 Adding question to database...", questionToAdd);
        const addedQuestion = await questionsAPI.add(
          currentTestTab,
          questionToAdd
        );
        console.log("✅ Question added successfully:", addedQuestion);

        // Update local state safely - force reload from API to ensure consistency
        const freshQuestions = await questionsAPI.getByType(currentTestTab);
        setCurrentQuestions(freshQuestions);
        console.log(
          `✅ Reloaded ${freshQuestions.length} questions from database`
        );

        // Reset form based on test type
        if (currentTestTab === "eq") {
          setNewQuestion({ statement: "", type: "" });
        } else if (currentTestTab === "holland") {
          setNewQuestion({ activity: "", type: "" });
        } else {
          // Set number of options based on test type
          const numOptions = currentTestTab === "mbti" ? 2 : 4;
          const options = Array.from({ length: numOptions }, () => ({
            text: "",
            value: "",
            option_text: "",
            option_value: "",
            correct_answer: 0,
          }));

          setNewQuestion({
            question: "",
            options: options,
          });
        }

        // Hide form and show success notification
        setShowAddQuestionForm(false);
        showNotification(
          `✅ Đã thêm câu hỏi mới cho ${
            testTabs.find((t) => t.id === currentTestTab)?.label
          } thành công!`,
          "success"
        );
      } catch (error) {
        console.error("❌ Failed to add question to database:", error);

        // Show user-friendly error message
        let errorMessage = "❌ Có lỗi xảy ra khi thêm câu hỏi.";
        if (
          error.message.includes("network") ||
          error.message.includes("fetch")
        ) {
          errorMessage = "❌ Lỗi kết nối mạng. Vui lòng thử lại.";
        } else if (
          error.message.includes("database") ||
          error.message.includes("SQL")
        ) {
          errorMessage = "❌ Lỗi cơ sở dữ liệu. Vui lòng liên hệ admin.";
        } else if (error.message) {
          errorMessage = `❌ Lỗi: ${error.message}`;
        }

        showNotification(errorMessage, "error");
      } finally {
        // Always clear loading state
        setLoading(false);
      }
    } else {
      if (currentTestTab === "eq") {
        showNotification(
          "❌ Vui lòng điền đầy đủ statement và type cho EQ!",
          "error"
        );
      } else if (currentTestTab === "holland") {
        showNotification(
          "❌ Vui lòng điền đầy đủ activity và type cho Holland!",
          "error"
        );
      } else {
        if (currentTestTab === "iq") {
          const correctAnswers =
            newQuestion.options?.filter((opt) => opt.correct_answer === 1) ||
            [];
          if (correctAnswers.length === 0) {
            showNotification(
              "❌ Vui lòng chọn một đáp án đúng cho câu hỏi IQ!",
              "error"
            );
          } else if (correctAnswers.length > 1) {
            showNotification(
              "❌ Chỉ được chọn một đáp án đúng cho câu hỏi IQ!",
              "error"
            );
          } else {
            showNotification(
              "❌ Vui lòng điền đầy đủ câu hỏi và các tùy chọn!",
              "error"
            );
          }
        } else {
          showNotification(
            "❌ Vui lòng điền đầy đủ câu hỏi và các tùy chọn!",
            "error"
          );
        }
      }
    }
  };

  const deleteQuestion = async (id) => {
    if (
      window.confirm("Bạn có chắc chắn muốn xóa câu hỏi này khỏi database?")
    ) {
      try {
        // Delete from database via API
        await questionsAPI.delete(id, currentTestTab);
        console.log("✅ Deleted question from database:", id);

        // Update local state
        const updatedQuestions = getCurrentQuestions().filter(
          (q) => q.id !== id
        );
        setCurrentQuestions(updatedQuestions);
        showNotification(
          "🗑️ Đã xóa câu hỏi khỏi database thành công!",
          "success"
        );
      } catch (error) {
        console.error("❌ Failed to delete question from database:", error);
        showNotification("❌ Lỗi khi xóa câu hỏi khỏi database!", "error");
      }
    }
  };

  const startEdit = (question) => {
    setEditingQuestion({
      ...question,
      options:
        question.options?.map((opt) => ({
          ...opt,
          text: opt.option_text || opt.text || "",
          value: opt.option_value || opt.value || "",
          option_text: opt.option_text || opt.text || "",
          option_value: opt.option_value || opt.value || "",
        })) || [],
    });
    showNotification("✏️ Đang chỉnh sửa câu hỏi...", "info");
  };

  const saveEdit = async () => {
    try {
      // Update in database via API
      await questionsAPI.update(editingQuestion.id, editingQuestion);
      console.log("✅ Updated question in database:", editingQuestion.id);

      // Update local state
      const updatedQuestions = getCurrentQuestions().map((q) =>
        q.id === editingQuestion.id ? editingQuestion : q
      );
      setCurrentQuestions(updatedQuestions);
      setEditingQuestion(null);
      showNotification(
        "💾 Đã lưu thay đổi câu hỏi vào database thành công!",
        "success"
      );
    } catch (error) {
      console.error("❌ Failed to update question in database:", error);
      showNotification("❌ Lỗi khi cập nhật câu hỏi trong database!", "error");
    }
  };

  const cancelEdit = () => {
    setEditingQuestion(null);
    showNotification("↩️ Đã hủy chỉnh sửa câu hỏi!", "info");
  };

  const filteredQuestions = getCurrentQuestions().filter((q) => {
    const searchField = q.question || q.statement || q.activity || "";
    return searchField.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getQuestionTemplate = () => {
    switch (currentTestTab) {
      case "mbti":
        return {
          placeholder: "Nhập câu hỏi MBTI...",
          valueOptions: ["E", "I", "S", "N", "T", "F", "J", "P"],
        };
      case "iq":
        return {
          placeholder: "Nhập câu hỏi IQ...",
          valueOptions: ["A", "B", "C", "D"],
        };
      case "eq":
        return {
          placeholder: "Nhập câu hỏi EQ...",
          valueOptions: ["A", "B", "C", "D"],
        };
      case "disc":
        return {
          placeholder: "Nhập câu hỏi DISC...",
          valueOptions: ["D", "I", "S", "C"],
        };
      case "holland":
        return {
          placeholder: "Nhập câu hỏi Holland...",
          valueOptions: ["R", "I", "A", "S", "E", "C"],
        };
      default:
        return {
          placeholder: "Nhập câu hỏi...",
          valueOptions: ["A", "B", "C", "D"],
        };
    }
  };

  // Authentication handled by SecureAuth wrapper - removed old login screen

  // Data management functions
  const exportData = () => {
    try {
      const allData = {
        questions: {
          mbti: mbtiQuestions,
          iq: iqQuestions,
          eq: eqQuestions,
          disc: discQuestions,
          holland: hollandQuestions,
        },
        customContent: customContent,
        exportDate: new Date().toISOString(),
        version: "1.0",
      };

      const dataStr = JSON.stringify(allData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `mbti-data-backup-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showNotification("✅ Đã xuất dữ liệu thành công!", "success");
    } catch (error) {
      console.error("Error exporting data:", error);
      showNotification("❌ Lỗi khi xuất dữ liệu!", "error");
    }
  };

  const importData = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const importedData = JSON.parse(e.target.result);

            // Validate data structure
            if (!importedData.questions || !importedData.customContent) {
              throw new Error("Invalid data format");
            }

            // Confirm import
            const confirmImport = window.confirm(
              `Bạn có chắc muốn import dữ liệu?\n\n` +
                `Dữ liệu xuất: ${
                  importedData.exportDate
                    ? new Date(importedData.exportDate).toLocaleString("vi-VN")
                    : "Không rõ"
                }\n` +
                `Phiên bản: ${importedData.version || "Không rõ"}\n\n` +
                `⚠️ Dữ liệu hiện tại sẽ bị ghi đè!`
            );

            if (confirmImport) {
              // Import questions
              Object.keys(importedData.questions).forEach((testType) => {
                localStorage.setItem(
                  `questions_${testType}`,
                  JSON.stringify(importedData.questions[testType])
                );
              });

              // Import custom content
              localStorage.setItem(
                "customContent",
                JSON.stringify(importedData.customContent)
              );

              // Reload data - update state
              if (importedData.questions.mbti)
                setMbtiQuestions(importedData.questions.mbti);
              if (importedData.questions.iq)
                setIqQuestions(importedData.questions.iq);
              if (importedData.questions.eq)
                setEqQuestions(importedData.questions.eq);
              if (importedData.questions.disc)
                setDiscQuestions(importedData.questions.disc);
              if (importedData.questions.holland)
                setHollandQuestions(importedData.questions.holland);
              setCustomContent(importedData.customContent);

              showNotification(
                "✅ Đã import dữ liệu thành công! Vui lòng refresh trang để thấy thay đổi.",
                "success"
              );

              // Auto refresh after 2 seconds
              setTimeout(() => {
                window.location.reload();
              }, 2000);
            }
          } catch (error) {
            console.error("Error importing data:", error);
            showNotification(
              "❌ Lỗi khi import dữ liệu! Vui lòng kiểm tra định dạng file.",
              "error"
            );
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  // Dashboard Content
  const renderDashboard = () => (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-800">Dashboard</h2>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">Xin chào, Admin</div>
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
        </div>
      </div>

      {/* Storage Monitor */}
      <div className="mb-8">
        <StorageMonitor />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {testTabs.map((test, index) => (
          <div
            key={test.id}
            className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div
                className={`w-12 h-12 bg-gradient-to-r ${test.color} rounded-xl flex items-center justify-center text-white text-xl`}
              >
                {test.icon}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-800">
                  {(() => {
                    switch (test.id) {
                      case "mbti":
                        return mbtiQuestions?.length || 0;
                      case "iq":
                        return iqQuestions?.length || 0;
                      case "eq":
                        return eqQuestions?.length || 0;
                      case "disc":
                        return discQuestions?.length || 0;
                      case "holland":
                        return hollandQuestions?.length || 0;
                      default:
                        return 0;
                    }
                  })()}
                </div>
                <div className="text-sm text-gray-500">Câu hỏi</div>
              </div>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">{test.label}</h3>
            <div className="flex items-center text-sm text-gray-500">
              <svg
                className="w-4 h-4 mr-1 text-green-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Hoạt động
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <h3 className="text-xl font-bold text-gray-800 mb-6">Thao tác nhanh</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <button
            onClick={() => setCurrentTab("questions")}
            className="p-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 text-left"
          >
            <div className="font-semibold mb-1">Quản lý câu hỏi</div>
            <div className="text-blue-100 text-sm">
              Thêm, sửa, xóa câu hỏi test
            </div>
          </button>

          <button
            onClick={() => setCurrentTab("content")}
            className="p-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all duration-300 text-left"
          >
            <div className="font-semibold mb-1">Nội dung tùy chỉnh</div>
            <div className="text-purple-100 text-sm">
              Chỉnh sửa hướng dẫn test
            </div>
          </button>

          <button
            onClick={() => setCurrentTab("statistics")}
            className="p-6 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 text-left"
          >
            <div className="font-semibold mb-1">Thống kê</div>
            <div className="text-green-100 text-sm">Xem báo cáo hệ thống</div>
          </button>
        </div>

        {/* Data Management Section */}
        <div className="border-t pt-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">
            🔄 Quản lý dữ liệu
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <button
              onClick={exportData}
              className="p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 text-left"
            >
              <div className="font-semibold mb-1">📁 Xuất dữ liệu</div>
              <div className="text-orange-100 text-sm">
                Tải file backup JSON
              </div>
            </button>

            <button
              onClick={importData}
              className="p-4 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl hover:from-indigo-600 hover:to-indigo-700 transition-all duration-300 text-left"
            >
              <div className="font-semibold mb-1">📂 Nhập dữ liệu</div>
              <div className="text-indigo-100 text-sm">
                Khôi phục từ file backup
              </div>
            </button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="text-amber-400 mr-3 mt-0.5">💡</div>
              <div className="text-sm text-amber-800">
                <strong>Lưu ý:</strong> Để đồng bộ dữ liệu giữa các cổng khác
                nhau (localhost:3000, localhost:3002...), hãy xuất dữ liệu từ
                cổng có đầy đủ data và nhập vào cổng khác. Dữ liệu localStorage
                được lưu riêng biệt cho mỗi cổng.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Markdown renderer component
  const MarkdownRenderer = ({ content }) => {
    if (!content) return null;

    // Parse markdown content
    const parseMarkdown = (text) => {
      const elements = [];
      const lines = text.split("\n");
      let currentIndex = 0;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Check for image
        const imageMatch = line.match(/!\[([^\]]*)\]\(([^)]+)\)/);
        if (imageMatch) {
          const [, alt, src] = imageMatch;
          elements.push(
            <div
              key={currentIndex++}
              style={{
                margin: "0.5rem 0",
                border: "1px solid #e5e7eb",
                borderRadius: "0.5rem",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <img
                src={src}
                alt={alt || "Hình ảnh"}
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  display: "block",
                  background: "#f9fafb",
                  minHeight: "100px",
                }}
                onLoad={(e) => {
                  e.target.parentElement.style.borderColor = "#10b981";
                  console.log("✅ Image loaded successfully:", src);
                }}
                onError={(e) => {
                  e.target.parentElement.style.borderColor = "#ef4444";
                  e.target.parentElement.style.backgroundColor = "#fef2f2";
                  e.target.style.display = "none";
                  e.target.parentElement.innerHTML =
                    '<div style="padding: 1rem; text-align: center; color: #dc2626; font-size: 14px;">❌ Không thể tải hình ảnh<br/><small>' +
                    (alt || "Hình ảnh") +
                    "</small></div>";
                  console.error("❌ Image failed to load:", src);
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "linear-gradient(transparent, rgba(0,0,0,0.5))",
                  color: "white",
                  padding: "0.25rem 0.5rem",
                  fontSize: "11px",
                  opacity: 0.8,
                }}
              >
                {alt || "Hình ảnh"}
              </div>
            </div>
          );
          continue;
        }

        // Check for headers
        if (line.startsWith("# ")) {
          elements.push(
            <h1
              key={currentIndex++}
              style={{
                fontSize: "1.25rem",
                fontWeight: "bold",
                marginBottom: "0.5rem",
              }}
            >
              {line.substring(2)}
            </h1>
          );
          continue;
        }
        if (line.startsWith("## ")) {
          elements.push(
            <h2
              key={currentIndex++}
              style={{
                fontSize: "1.125rem",
                fontWeight: "600",
                marginBottom: "0.5rem",
              }}
            >
              {line.substring(3)}
            </h2>
          );
          continue;
        }

        // Check for lists
        if (line.startsWith("- ")) {
          elements.push(
            <li key={currentIndex++} style={{ marginLeft: "1rem" }}>
              • {line.substring(2)}
            </li>
          );
          continue;
        }

        // Process inline formatting for regular text
        if (line.trim()) {
          // Simple approach to avoid infinite re-render
          let processedText = line;
          const inlineElements = [];

          // Handle HTML span tags with style (color, font-size, etc.)
          const lineIndex = currentIndex;
          processedText = processedText.replace(
            /<span\s+style="([^"]*)">(.*?)<\/span>/g,
            (match, style, content) => {
              const id = `span_${lineIndex}_${inlineElements.length}`;
              const styleObj = {};

              // Parse style string into object
              style.split(";").forEach((prop) => {
                const [key, value] = prop.split(":");
                if (key && value) {
                  const cleanKey = key
                    .trim()
                    .replace(/-(.)/g, (_, letter) => letter.toUpperCase());
                  styleObj[cleanKey] = value.trim();
                }
              });

              inlineElements.push({
                id,
                element: (
                  <span key={id} style={styleObj}>
                    {content}
                  </span>
                ),
              });
              return `{${id}}`;
            }
          );

          // Handle underline tags
          processedText = processedText.replace(
            /<u>(.*?)<\/u>/g,
            (match, content) => {
              const id = `underline_${lineIndex}_${inlineElements.length}`;
              inlineElements.push({ id, element: <u key={id}>{content}</u> });
              return `{${id}}`;
            }
          );

          // Handle bold **text**
          processedText = processedText.replace(
            /\*\*(.*?)\*\*/g,
            (match, content) => {
              const id = `bold_${lineIndex}_${inlineElements.length}`;
              inlineElements.push({
                id,
                element: <strong key={id}>{content}</strong>,
              });
              return `{${id}}`;
            }
          );

          // Handle italic *text*
          processedText = processedText.replace(
            /\*(.*?)\*/g,
            (match, content) => {
              const id = `italic_${lineIndex}_${inlineElements.length}`;
              inlineElements.push({ id, element: <em key={id}>{content}</em> });
              return `{${id}}`;
            }
          );

          // Handle links [text](url)
          processedText = processedText.replace(
            /\[([^\]]+)\]\(([^)]+)\)/g,
            (match, linkText, url) => {
              const id = `link_${lineIndex}_${inlineElements.length}`;
              inlineElements.push({
                id,
                element: (
                  <a
                    key={id}
                    href={url}
                    style={{ color: "#2563eb", textDecoration: "underline" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {linkText}
                  </a>
                ),
              });
              return `{${id}}`;
            }
          );

          // Build final content
          const buildContent = () => {
            const parts = processedText.split(/(\{[^}]+\})/);
            return parts
              .map((part, partIndex) => {
                if (part.startsWith("{") && part.endsWith("}")) {
                  const id = part.slice(1, -1);
                  const found = inlineElements.find((el) => el.id === id);
                  return found ? found.element : part;
                }
                return part;
              })
              .filter((part) => part !== "");
          };

          elements.push(
            <p key={currentIndex++} style={{ marginBottom: "0.5rem" }}>
              {buildContent()}
            </p>
          );
        }
      }

      return elements;
    };

    return (
      <div className="prose prose-sm max-w-none">{parseMarkdown(content)}</div>
    );
  };

  // Content Management handlers
  const handleEditorChange = (content) => {
    setTempContent(content);
    setHasUnsavedChanges(true);
  };

  // Refresh preview function
  const refreshPreview = () => {
    // Force refresh by clearing and resetting content
    const currentContent = tempContent;
    setTempContent('');
    setTimeout(() => {
      setTempContent(currentContent);
      showNotification("🔄 Preview đã được làm mới!", "success");
    }, 100);
  };

  const handleSaveContent = () => {
    let newContent;

    // If the section uses templates (e.g., MBTI), update the selected template
    const section = customContent?.[selectedTest] || {};
    if (section.templates && typeof section.templates === "object") {
      const updatedTemplates = { ...(section.templates || {}) };
      const tplId = selectedTemplateId || section.activeTemplateId;
      const currentTpl = updatedTemplates[tplId] || { name: "Mẫu", instructions: "" };
      updatedTemplates[tplId] = { ...currentTpl, instructions: tempContent };

      newContent = {
        ...customContent,
        [selectedTest]: {
          ...section,
          templates: updatedTemplates,
          activeTemplateId: activeTemplateId || tplId,
        },
      };
    } else {
      // Legacy single-content mode
      newContent = {
        ...customContent,
        [selectedTest]: {
          ...section,
          instructions: tempContent,
        },
      };
    }
    saveCustomContent(newContent);
    setHasUnsavedChanges(false);
    showNotification(
      `💾 Đã lưu nội dung cho ${
        testTabs.find((t) => t.id === selectedTest)?.label
      } thành công!`,
      "success"
    );
  };

  // Save currently selected template as active immediately (auto-save)
  const saveActiveTemplateNow = async () => {
    setSettingActiveLoading(true);
    try {
      const section = customContent?.[selectedTest] || {};

      // Build new section ensuring templates structure exists and
      // also persist current editor content for the selected template
      let newSection;
      if (section.templates && typeof section.templates === "object") {
        const updatedTemplates = { ...(section.templates || {}) };
        const tplId = selectedTemplateId || section.activeTemplateId || "default";
        const currentTpl = updatedTemplates[tplId] || { name: "Mẫu", instructions: "" };
        updatedTemplates[tplId] = { ...currentTpl, instructions: tempContent };
        newSection = {
          ...section,
          templates: updatedTemplates,
          activeTemplateId: tplId,
          activeTemplateStartedAt: new Date().toISOString(),
        };
      } else {
        // Legacy → migrate to templates using current editor content
        newSection = {
          templates: {
            default: {
              name: "Mặc định",
              instructions: tempContent || section.instructions || "",
            },
          },
          activeTemplateId: "default",
          activeTemplateStartedAt: new Date().toISOString(),
        };
      }

      const newContent = { ...customContent, [selectedTest]: newSection };
      // Reuse common save (handles DB + localStorage + optional reload)
      await saveCustomContent(newContent, { reload: false, activityType: 'set_active_template' });
      setActiveTemplateId(newSection.activeTemplateId);
      setHasUnsavedChanges(false);
      showNotification("✅ Đã đặt làm mẫu hiển thị và tự động lưu!", "success");
    } catch (e) {
      console.error("❌ Auto-save active template failed:", e);
      showNotification("❌ Không thể lưu mẫu hiển thị!", "error");
    } finally {
      setSettingActiveLoading(false);
    }
  };

  // Delete current template (safeguards: cannot delete active or last template)
  const deleteCurrentTemplate = async () => {
    try {
      const section = customContent?.[selectedTest] || {};
      const templates = section.templates && typeof section.templates === 'object'
        ? { ...section.templates }
        : { default: { name: 'Mặc định', instructions: section.instructions || '' } };

      const templateIds = Object.keys(templates);
      const activeId = section.activeTemplateId || (templateIds[0] || 'default');
      const tplId = selectedTemplateId;

      if (templateIds.length <= 1) {
        showNotification('❌ Cần ít nhất 1 mẫu. Không thể xóa.', 'error');
        return;
      }
      if (tplId === activeId) {
        showNotification('❌ Không thể xóa mẫu đang hiển thị. Hãy chuyển sang mẫu khác trước.', 'error');
        return;
      }

      const confirmDelete = window.confirm('Bạn chắc chắn muốn xóa mẫu này? Thao tác không thể hoàn tác.');
      if (!confirmDelete) return;

      delete templates[tplId];

      // Choose next selected template
      const remainingIds = Object.keys(templates);
      const nextId = remainingIds.includes('default') ? 'default' : remainingIds[0];

      const newSection = {
        ...section,
        templates,
        // Keep activeTemplateId as is
      };

      const updatedContent = { ...customContent, [selectedTest]: newSection };
      setCustomContent(updatedContent);
      setSelectedTemplateId(nextId);
      await saveCustomContent(updatedContent, { reload: false });
      showNotification('🗑️ Đã xóa mẫu.', 'success');
    } catch (e) {
      console.error('Delete template error:', e);
      showNotification('❌ Không thể xóa mẫu!', 'error');
    }
  };

  // Content Management
  const renderContentManagement = () => {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-800">
            Quản lý nội dung tùy chỉnh
          </h2>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6">


          {/* Test Tabs */}
          <div className="flex flex-wrap gap-4 mb-6">
            {testTabs.map((test) => (
              <button
                key={test.id}
                onClick={() => {
                  setSelectedTest(test.id);
                  localStorage.setItem("admin_selected_test", test.id);
                }}
                className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
                  selectedTest === test.id
                    ? `bg-gradient-to-r ${test.color} text-white shadow-lg`
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <span>{test.icon}</span>
                <span>{test.label}</span>
              </button>
            ))}
          </div>

          <div className="space-y-6">
            {/* Templates toolbar for tests that support multi-templates */}
            {templatableTests.includes(selectedTest) && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100">
                <div className="flex-1 flex items-center gap-3 flex-wrap">
                  <label className="text-sm font-semibold text-gray-700">Mẫu nội dung:</label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => {
                      setSelectedTemplateId(e.target.value);
                      localStorage.setItem("admin_selected_template", e.target.value);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800"
                  >
                    {(() => {
                      const section = customContent?.[selectedTest] || {};
                      const templates = section.templates && typeof section.templates === "object"
                        ? section.templates
                        : { default: { name: "Mặc định", instructions: section.instructions || "" } };
                      return Object.entries(templates).map(([id, tpl]) => (
                        <option key={id} value={id}>
                          {tpl?.name || `Mẫu ${id}`}
                          {section.activeTemplateId === id || (!section.templates && id === 'default') ? " (đang hiển thị)" : ""}
                        </option>
                      ));
                    })()}
                  </select>

                  <button
                    type="button"
                    onClick={() => {
                      // Ensure template structure exists (migrate if needed)
                      const section = customContent?.[selectedTest] || {};
                      const hasTemplates = section.templates && typeof section.templates === "object";
                      const migrated = hasTemplates
                        ? section
                        : {
                            templates: {
                              default: {
                                name: "Mặc định",
                                instructions: section.instructions || "",
                              },
                            },
                            activeTemplateId: "default",
                          };

                      const name = window.prompt("Tên mẫu mới", "Mẫu mới");
                      const id = `tpl_${Date.now()}`;
                      const newSection = {
                        ...migrated,
                        templates: {
                          ...migrated.templates,
                          [id]: {
                            name: name && name.trim() ? name.trim() : `Mẫu ${Object.keys(migrated.templates).length + 1}`,
                            instructions: "",
                          },
                        },
                      };

                      const updated = { ...customContent, [selectedTest]: newSection };
                      setCustomContent(updated);
                      setSelectedTemplateId(id);
                      setActiveTemplateId(newSection.activeTemplateId || "default");
                      setTempContent("");
                      setHasUnsavedChanges(true);
                    }}
                    className="px-3 py-2 rounded-lg bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  >
                    ➕ Tạo mẫu mới
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      const section = customContent?.[selectedTest] || {};
                      const templates = section.templates && typeof section.templates === 'object'
                        ? section.templates
                        : { default: { name: 'Mặc định', instructions: section.instructions || '' } };
                      
                      const currentTemplate = templates[selectedTemplateId];
                      if (!currentTemplate) {
                        showNotification('❌ Không tìm thấy mẫu!', 'error');
                        return;
                      }

                      const newName = window.prompt('Đổi tên mẫu:', currentTemplate.name || 'Mẫu');
                      if (!newName || !newName.trim()) {
                        showNotification('❌ Tên mẫu không được để trống!', 'error');
                        return;
                      }

                      const updatedTemplates = {
                        ...templates,
                        [selectedTemplateId]: {
                          ...currentTemplate,
                          name: newName.trim(),
                        },
                      };

                      const newSection = {
                        ...section,
                        templates: updatedTemplates,
                      };

                      const updatedContent = { ...customContent, [selectedTest]: newSection };
                      setCustomContent(updatedContent);
                      saveCustomContent(updatedContent, { reload: false });
                      showNotification('✅ Đã đổi tên mẫu!', 'success');
                    }}
                    className="px-3 py-2 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    ✏️ Đổi tên
                  </button>

                  {(() => {
                    const section = customContent?.[selectedTest] || {};
                    const templates = section.templates && typeof section.templates === 'object' ? section.templates : { default: { name: 'Mặc định' } };
                    const templateIds = Object.keys(templates);
                    const activeId = section.activeTemplateId || (templateIds[0] || 'default');
                    const canDelete = templateIds.length > 1 && selectedTemplateId !== activeId;
                    return (
                      <button
                        type="button"
                        onClick={deleteCurrentTemplate}
                        disabled={!canDelete}
                        className={`px-3 py-2 rounded-lg border ${
                          canDelete
                            ? 'bg-white text-red-600 border-red-300 hover:bg-red-50'
                            : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        }`}
                        title={canDelete ? 'Xóa mẫu hiện tại' : 'Không thể xóa mẫu đang hiển thị hoặc khi chỉ còn 1 mẫu'}
                      >
                        🗑️ Xóa mẫu
                      </button>
                    );
                  })()}
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={saveActiveTemplateNow}
                    disabled={(() => {
                      const section = customContent?.[selectedTest] || {};
                      const activeId = section.activeTemplateId || selectedTemplateId;
                      return settingActiveLoading || activeId === selectedTemplateId;
                    })()}
                    className={`px-3 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${(() => {
                      const section = customContent?.[selectedTest] || {};
                      const activeId = section.activeTemplateId || selectedTemplateId;
                      const isActive = activeId === selectedTemplateId;
                      if (settingActiveLoading) return 'bg-gray-300 text-gray-600 cursor-wait';
                      if (isActive) return 'bg-gray-200 text-gray-500 cursor-not-allowed';
                      return 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700';
                    })()}`}
                  >
                    {settingActiveLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        <span>Đang đặt…</span>
                      </>
                    ) : (() => {
                      const section = customContent?.[selectedTest] || {};
                      const activeId = section.activeTemplateId || selectedTemplateId;
                      return <span>{activeId === selectedTemplateId ? 'Đang hiển thị' : 'Đặt làm mẫu hiển thị'}</span>;
                    })()}
                  </button>
                </div>
              </div>
            )}


            {/* Auto-rotate configuration for current template */}
            {templatableTests.includes(selectedTest) && (() => {
              const section = customContent?.[selectedTest] || {};
              const templates = section.templates && typeof section.templates === 'object'
                ? section.templates
                : { default: { name: 'Mặc định', instructions: section.instructions || '' } };
              const tpl = templates[selectedTemplateId] || {};
              const auto = tpl.autoRotate || { enabled: false, quota: 0, nextTemplateId: '' };
              const otherTemplateIds = Object.keys(templates).filter(id => id !== selectedTemplateId);


              return (
                <div className="p-4 bg-white rounded-xl border border-gray-200">
                  {/* Template Password Section */}
                  <div className="mb-4 pb-4 border-b border-gray-100">
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Mật khẩu riêng cho mẫu này
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={tpl.password || ''}
                        placeholder="Để trống nếu muốn dùng mật khẩu chung"
                        onChange={(e) => {
                          const password = e.target.value.trim() || null;
                          const newTpl = { ...tpl, password };
                          const newSection = { ...section, templates: { ...templates, [selectedTemplateId]: newTpl } };
                          const updatedContent = { ...customContent, [selectedTest]: newSection };
                          setCustomContent(updatedContent);
                          setHasUnsavedChanges(true);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {tpl.password && (
                        <span className="text-xs text-green-600 font-medium">✅ Đang dùng mật khẩu riêng</span>
                      )}
                      {!tpl.password && (
                        <span className="text-xs text-gray-500">→ Sẽ dùng mật khẩu chung</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Mật khẩu riêng cho mẫu này. Nếu để trống, hệ thống sẽ dùng mật khẩu chung trong phần "Cài đặt".
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      id="auto-rotate-enabled"
                      type="checkbox"
                      checked={!!auto.enabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        const newTpl = { ...tpl, autoRotate: { ...auto, enabled } };
                        const newSection = {
                          ...section,
                          templates: { ...templates, [selectedTemplateId]: newTpl },
                        };
                        const updatedContent = { ...customContent, [selectedTest]: newSection };
                        setCustomContent(updatedContent);
                        setHasUnsavedChanges(true);
                      }}
                      className="w-4 h-4"
                    />
                    <label htmlFor="auto-rotate-enabled" className="font-semibold text-gray-800">
                      Tự động đổi mẫu khi đạt lượt xác thực Google
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Ngưỡng (lượt xác thực)</label>
                      <input
                        type="number"
                        min={1}
                        value={Number(auto.quota || 0)}
                        onChange={(e) => {
                          const quota = Math.max(1, parseInt(e.target.value || '1', 10));
                          const newTpl = { ...tpl, autoRotate: { ...auto, quota } };
                          const newSection = { ...section, templates: { ...templates, [selectedTemplateId]: newTpl } };
                          const updatedContent = { ...customContent, [selectedTest]: newSection };
                          setCustomContent(updatedContent);
                          setHasUnsavedChanges(true);
                        }}
                        disabled={!auto.enabled}
                        className={`w-full px-3 py-2 border rounded-lg ${auto.enabled ? 'border-gray-300' : 'bg-gray-100 text-gray-400 border-gray-200'}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm text-gray-600 mb-1">Sau đó chuyển đến mẫu</label>
                      <select
                        value={auto.nextTemplateId || ''}
                        onChange={(e) => {
                          const nextTemplateId = e.target.value;
                          const newTpl = { ...tpl, autoRotate: { ...auto, nextTemplateId } };
                          const newSection = { ...section, templates: { ...templates, [selectedTemplateId]: newTpl } };
                          const updatedContent = { ...customContent, [selectedTest]: newSection };
                          setCustomContent(updatedContent);
                          setHasUnsavedChanges(true);
                        }}
                        disabled={!auto.enabled || otherTemplateIds.length === 0}
                        className={`w-full px-3 py-2 border rounded-lg ${auto.enabled ? 'border-gray-300' : 'bg-gray-100 text-gray-400 border-gray-200'}`}
                      >
                        <option value="" disabled={true}>Chọn mẫu tiếp theo</option>
                        {otherTemplateIds.map((id) => (
                          <option key={id} value={id}>
                            {templates[id]?.name || id}
                          </option>
                        ))}
                      </select>
                      {otherTemplateIds.length === 0 && (
                        <div className="text-xs text-gray-500 mt-1">Hãy tạo ít nhất 1 mẫu khác để dùng chức năng này.</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    {section.activeTemplateStartedAt && (
                      <div className="text-xs text-gray-500">
                        Bắt đầu đếm lượt cho mẫu đang hiển thị từ: {new Date(section.activeTemplateStartedAt).toLocaleString('vi-VN')}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        setLoading(true);
                        try {
                          // Optimized: Chỉ gửi template hiện tại (vài KB) thay vì toàn bộ (30MB)
                          const currentTemplate = templates[selectedTemplateId];
                          await saveAutoRotateConfig(selectedTest, selectedTemplateId, currentTemplate);
                        } catch (e) {
                          console.error('Save error:', e);
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Đang lưu...
                        </>
                      ) : 'Lưu'}
                    </button>
                  </div>

                  {/* Display current verification count and daily switch count */}
                  {auto.enabled && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm text-blue-800">
                            <strong>Lượt xác thực hiện tại:</strong> {currentVerificationCount}
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            (Cho mẫu hiện tại)
                          </p>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                          <p className="text-sm text-purple-800">
                            <strong>Lượt xác thực tổng:</strong> {totalVerificationCount}
                          </p>
                          <p className="text-xs text-purple-600 mt-1">
                            (Tổng trong ngày)
                          </p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-green-800">
                            <strong>Số lần đổi mẫu hôm nay:</strong> {dailySwitchCount}
                          </p>
                        </div>
                      </div>

                      {/* Button to view activity log */}
                      <div className="mt-4">
                        <button
                          onClick={() => {
                            setShowActivityLog(true);
                            loadActivityLog(0);
                          }}
                          className="px-4 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2 text-sm font-medium shadow-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Xem lịch sử hoạt động
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            <div>
              <label className="block text-lg font-semibold mb-3 text-gray-800">
                Nội dung hiển thị cho{" "}
                {testTabs.find((t) => t.id === selectedTest)?.label}
              </label>

              {/* Custom Classic Editor - Dựa trên WordPress Classic Editor source code */}
              <ClassicEditor
                value={tempContent}
                onChange={handleEditorChange}
              />
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {hasUnsavedChanges && (
                  <span className="text-orange-600 flex items-center">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.864-.833-2.634 0L4.168 18.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    Có thay đổi chưa được lưu
                  </span>
                )}
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={refreshPreview}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
                  title="Làm mới preview nếu hình ảnh không hiển thị"
                >
                  🔄 Làm mới
                </button>
                <button
                  onClick={handleSaveContent}
                  disabled={!hasUnsavedChanges}
                  className={`font-bold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                    hasUnsavedChanges
                      ? `bg-gradient-to-r ${
                          testTabs.find((t) => t.id === selectedTest)?.color
                        } text-white hover:shadow-lg`
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {hasUnsavedChanges ? "Lưu thay đổi" : "Đã lưu"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get template for current test
  const template = getQuestionTemplate();

  // Question Management with full CRUD
  const renderQuestionManagement = () => {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-800">Quản lý câu hỏi</h2>
          <button
            onClick={openAddForm}
            className={`font-bold py-2 px-4 rounded-xl transition-colors ${
              showAddQuestionForm
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {showAddQuestionForm ? "Ẩn form thêm câu hỏi" : "Thêm câu hỏi mới"}
          </button>
        </div>

        {/* Test Tabs */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex flex-wrap gap-4 mb-6">
            {testTabs.map((test) => (
              <button
                key={test.id}
                onClick={() => {
                  setCurrentTestTab(test.id);
                  localStorage.setItem("admin_current_test_tab", test.id);
                  // Reset editing state when switching tabs
                  setEditingQuestion(null);
                  // Reset add question form when switching tabs
                  setShowAddQuestionForm(false);
                  if (test.id === "eq") {
                    setNewQuestion({ statement: "", type: "" });
                  } else if (test.id === "holland") {
                    setNewQuestion({ activity: "", type: "" });
                  } else {
                    // Set number of options based on test type
                    const numOptions = test.id === "mbti" ? 2 : 4;
                    const options = Array.from({ length: numOptions }, () => ({
                      text: "",
                      value: "",
                      option_text: "",
                      option_value: "",
                      correct_answer: 0,
                    }));

                    setNewQuestion({
                      question: "",
                      options: options,
                    });
                  }
                }}
                className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
                  currentTestTab === test.id
                    ? `bg-gradient-to-r ${test.color} text-white shadow-lg`
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <span>{test.icon}</span>
                <span>{test.label}</span>
                <span className="bg-white/20 px-2 py-1 rounded-full text-xs">
                  {(() => {
                    switch (test.id) {
                      case "mbti":
                        return mbtiQuestions?.length || 0;
                      case "iq":
                        return iqQuestions?.length || 0;
                      case "eq":
                        return eqQuestions?.length || 0;
                      case "disc":
                        return discQuestions?.length || 0;
                      case "holland":
                        return hollandQuestions?.length || 0;
                      default:
                        return 0;
                    }
                  })()}
                </span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="mb-6">
            <input
              type="text"
              placeholder="Tìm kiếm câu hỏi..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
            />
          </div>

          {/* Add New Question */}
          {showAddQuestionForm && (
            <div className="mb-8 p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                Thêm câu hỏi mới
              </h3>

              {/* Main input field - changes based on test type */}
              {currentTestTab === "eq" ? (
                <input
                  type="text"
                  placeholder="Nhập statement cho EQ test..."
                  value={newQuestion.statement || ""}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      statement: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 mb-4"
                />
              ) : currentTestTab === "holland" ? (
                <input
                  type="text"
                  placeholder="Nhập activity cho Holland test..."
                  value={newQuestion.activity || ""}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, activity: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 mb-4"
                />
              ) : (
                <input
                  type="text"
                  placeholder={template.placeholder}
                  value={newQuestion.question}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, question: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 mb-4"
                />
              )}

              {/* Type selector for EQ and Holland */}
              {(currentTestTab === "eq" || currentTestTab === "holland") && (
                <>
                  <select
                    value={newQuestion.type || ""}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, type: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 mb-4"
                  >
                    <option value="">Chọn loại</option>
                    {currentTestTab === "eq" ? (
                      <>
                        <option value="self_awareness">Self Awareness</option>
                        <option value="self_regulation">Self Regulation</option>
                        <option value="motivation">Motivation</option>
                        <option value="empathy">Empathy</option>
                        <option value="social_skills">Social Skills</option>
                      </>
                    ) : (
                      <>
                        <option value="R">Realistic</option>
                        <option value="I">Investigative</option>
                        <option value="A">Artistic</option>
                        <option value="S">Social</option>
                        <option value="E">Enterprising</option>
                        <option value="C">Conventional</option>
                      </>
                    )}
                  </select>

                  {/* Checkbox Đảo điểm - CHỈ cho Test EQ */}
                  {currentTestTab === "eq" && (
                    <div className="flex items-center space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl mb-4">
                      <input
                        type="checkbox"
                        id="new-reverse-scored"
                        checked={newQuestion.is_reverse_scored === 1}
                        onChange={(e) =>
                          setNewQuestion({
                            ...newQuestion,
                            is_reverse_scored: e.target.checked ? 1 : 0,
                          })
                        }
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <label
                        htmlFor="new-reverse-scored"
                        className="text-sm font-medium text-gray-700 cursor-pointer"
                      >
                        🔄 Đảo điểm (Reverse Scoring)
                      </label>
                      <span className="text-xs text-gray-500 ml-2">
                        (Công thức: 6 - điểm gốc)
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Options for different test types */}
              {["mbti", "iq", "disc"].includes(currentTestTab) &&
                Array.from({
                  length: currentTestTab === "mbti" ? 2 : 4,
                }).map((_, index) => {
                  const option = newQuestion.options[index] || {
                    text: "",
                    value: "",
                    option_text: "",
                    option_value: "",
                    correct_answer: 0,
                  };
                  return (
                    <div key={index} className="flex space-x-2 mb-3">
                      <input
                        type="text"
                        placeholder={`Tùy chọn ${String.fromCharCode(
                          65 + index
                        )}`}
                        value={option.option_text || option.text || ""}
                        onChange={(e) => {
                          const newOptions = [...newQuestion.options];
                          newOptions[index].option_text = e.target.value;
                          newOptions[index].text = e.target.value; // Keep both for compatibility
                          setNewQuestion({
                            ...newQuestion,
                            options: newOptions,
                          });
                        }}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                      />
                      <select
                        value={option.option_value || option.value || ""}
                        onChange={(e) => {
                          const newOptions = [...newQuestion.options];
                          newOptions[index].option_value = e.target.value;
                          newOptions[index].value = e.target.value; // Keep both for compatibility
                          setNewQuestion({
                            ...newQuestion,
                            options: newOptions,
                          });
                        }}
                        className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                      >
                        <option value="">Chọn giá trị</option>
                        {(template.valueOptions || []).map((val) => (
                          <option key={val} value={val}>
                            {val}
                          </option>
                        ))}
                      </select>
                      {/* Add correct answer checkbox for IQ test */}
                      {currentTestTab === "iq" && (
                        <label className="flex items-center space-x-2 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={option.correct_answer === 1}
                            onChange={(e) => {
                              const newOptions = [...newQuestion.options];
                              // Uncheck all others first
                              newOptions.forEach(
                                (opt) => (opt.correct_answer = 0)
                              );
                              // Check current one if checked
                              if (e.target.checked) {
                                newOptions[index].correct_answer = 1;
                              }
                              setNewQuestion({
                                ...newQuestion,
                                options: newOptions,
                              });
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-600">Đúng</span>
                        </label>
                      )}
                    </div>
                  );
                })}
              <button
                onClick={addQuestion}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Đang thêm...</span>
                  </div>
                ) : (
                  "Thêm câu hỏi"
                )}
              </button>
            </div>
          )}

          {/* Questions List */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800">
                Danh sách câu hỏi ({filteredQuestions.length})
              </h3>
            </div>

            {filteredQuestions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">
                  Chưa có câu hỏi nào cho{" "}
                  {testTabs.find((t) => t.id === currentTestTab)?.label}
                </p>
              </div>
            ) : (
              filteredQuestions.map((question, index) => (
                <div
                  key={question.id}
                  className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  {editingQuestion && editingQuestion.id === question.id ? (
                    <div className="space-y-4">
                      {/* Main field - changes based on test type */}
                      {currentTestTab === "eq" ? (
                        <input
                          type="text"
                          placeholder="Nhập statement cho EQ..."
                          value={
                            editingQuestion.statement ||
                            editingQuestion.question ||
                            ""
                          }
                          onChange={(e) =>
                            setEditingQuestion({
                              ...editingQuestion,
                              statement: e.target.value,
                              question: e.target.value, // ✅ FIX: Đồng bộ với question để backend nhận được
                              question_text: e.target.value, // ✅ FIX: Đồng bộ với question_text
                            })
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                        />
                      ) : currentTestTab === "holland" ? (
                        <input
                          type="text"
                          placeholder="Nhập activity cho Holland..."
                          value={
                            editingQuestion.activity ||
                            editingQuestion.question ||
                            ""
                          }
                          onChange={(e) =>
                            setEditingQuestion({
                              ...editingQuestion,
                              activity: e.target.value,
                              question: e.target.value, // ✅ FIX: Đồng bộ với question để backend nhận được
                              question_text: e.target.value, // ✅ FIX: Đồng bộ với question_text
                            })
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                        />
                      ) : (
                        <input
                          type="text"
                          value={editingQuestion.question}
                          onChange={(e) =>
                            setEditingQuestion({
                              ...editingQuestion,
                              question: e.target.value,
                              question_text: e.target.value, // ✅ FIX: Đồng bộ với question_text
                            })
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                        />
                      )}

                      {/* Type selector for EQ and Holland */}
                      {(currentTestTab === "eq" ||
                        currentTestTab === "holland") && (
                        <>
                          <select
                            value={editingQuestion.type || ""}
                            onChange={(e) =>
                              setEditingQuestion({
                                ...editingQuestion,
                                type: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                          >
                            <option value="">Chọn loại</option>
                            {currentTestTab === "eq" ? (
                              <>
                                <option value="self_awareness">
                                  Self Awareness
                                </option>
                                <option value="self_regulation">
                                  Self Regulation
                                </option>
                                <option value="motivation">Motivation</option>
                                <option value="empathy">Empathy</option>
                                <option value="social_skills">
                                  Social Skills
                                </option>
                              </>
                            ) : (
                              <>
                                <option value="R">Realistic</option>
                                <option value="I">Investigative</option>
                                <option value="A">Artistic</option>
                                <option value="S">Social</option>
                                <option value="E">Enterprising</option>
                                <option value="C">Conventional</option>
                            </>
                          )}
                        </select>

                        {/* Checkbox Đảo điểm - CHỈ cho Test EQ */}
                        {currentTestTab === "eq" && (
                          <div className="flex items-center space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                            <input
                              type="checkbox"
                              id="edit-reverse-scored"
                              checked={editingQuestion.is_reverse_scored === 1}
                              onChange={(e) =>
                                setEditingQuestion({
                                  ...editingQuestion,
                                  is_reverse_scored: e.target.checked ? 1 : 0,
                                })
                              }
                              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <label
                              htmlFor="edit-reverse-scored"
                              className="text-sm font-medium text-gray-700 cursor-pointer"
                            >
                              🔄 Đảo điểm (Reverse Scoring)
                            </label>
                            <span className="text-xs text-gray-500 ml-2">
                              (Công thức: 6 - điểm gốc)
                            </span>
                          </div>
                        )}
                      </>
                      )}

                      {/* Options only for MBTI, IQ, DISC */}
                      {["mbti", "iq", "disc"].includes(currentTestTab) &&
                        (editingQuestion.options || []).map(
                          (option, optIndex) => (
                            <div key={optIndex} className="flex space-x-2">
                              <input
                                type="text"
                                value={option.option_text || option.text || ""}
                                onChange={(e) => {
                                  const newOptions = [
                                    ...editingQuestion.options,
                                  ];
                                  newOptions[optIndex].option_text =
                                    e.target.value;
                                  newOptions[optIndex].text = e.target.value; // Keep both for compatibility
                                  setEditingQuestion({
                                    ...editingQuestion,
                                    options: newOptions,
                                  });
                                }}
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                              />
                              <select
                                value={
                                  option.option_value || option.value || ""
                                }
                                onChange={(e) => {
                                  const newOptions = [
                                    ...editingQuestion.options,
                                  ];
                                  newOptions[optIndex].option_value =
                                    e.target.value;
                                  newOptions[optIndex].value = e.target.value; // Keep both for compatibility
                                  setEditingQuestion({
                                    ...editingQuestion,
                                    options: newOptions,
                                  });
                                }}
                                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                              >
                                <option value="">Chọn giá trị</option>
                                {(template.valueOptions || []).map((val) => (
                                  <option key={val} value={val}>
                                    {val}
                                  </option>
                                ))}
                              </select>
                              {/* Add correct answer checkbox for IQ test in edit form */}
                              {currentTestTab === "iq" && (
                                <label className="flex items-center space-x-2 px-3 py-2 bg-green-50 rounded-xl border border-green-200">
                                  <input
                                    type="checkbox"
                                    checked={option.is_correct === 1}
                                    onChange={(e) => {
                                      const newOptions = [
                                        ...editingQuestion.options,
                                      ];
                                      // Uncheck all others first
                                      newOptions.forEach(
                                        (opt) => (opt.is_correct = 0)
                                      );
                                      // Check current one if checked
                                      if (e.target.checked) {
                                        newOptions[optIndex].is_correct = 1;
                                      }
                                      setEditingQuestion({
                                        ...editingQuestion,
                                        options: newOptions,
                                      });
                                    }}
                                    className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                  />
                                  <span className="text-sm text-green-700 font-medium">
                                    Đáp án đúng
                                  </span>
                                </label>
                              )}
                            </div>
                          )
                        )}
                      <div className="flex space-x-3">
                        <button
                          onClick={saveEdit}
                          className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-xl transition-all duration-300"
                        >
                          Lưu
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-xl transition-all duration-300"
                        >
                          Hủy
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800 mb-3 text-lg">
                            {index + 1}.{" "}
                            {(() => {
                              switch (currentTestTab) {
                                case "eq":
                                  return (
                                    question.statement || question.question
                                  );
                                case "holland":
                                  return question.activity || question.question;
                                default:
                                  return question.question;
                              }
                            })()}
                          </h4>

                          {/* Render options only for MBTI, IQ, DISC */}
                          {["mbti", "iq", "disc"].includes(currentTestTab) && (
                            <div className="space-y-2">
                              {(question.options || []).map(
                                (option, optIndex) => (
                                  <div
                                    key={optIndex}
                                    className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg"
                                  >
                                    <span className="text-gray-600 font-semibold w-6">
                                      {String.fromCharCode(65 + optIndex)}.
                                    </span>
                                    <span className="text-gray-700 flex-1">
                                      {option.option_text || option.text || ""}
                                    </span>
                                    <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                                      {option.option_value ||
                                        option.value ||
                                        ""}
                                    </span>
                                    {/* Show correct answer indicator for IQ questions */}
                                    {currentTestTab === "iq" &&
                                      option.is_correct === 1 && (
                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold ml-2">
                                          ✓ Đúng
                                        </span>
                                      )}
                                  </div>
                                )
                              )}
                            </div>
                          )}

                          {/* Render scale info for EQ and Holland */}
                          {currentTestTab === "eq" && (
                            <div className="p-3 bg-blue-50 rounded-lg space-y-2">
                              <p className="text-sm text-blue-700">
                                <strong>Type:</strong> {question.type || "N/A"}{" "}
                                |<strong> Scale:</strong> 1-5 Likert (Hoàn toàn
                                không đồng ý → Hoàn toàn đồng ý)
                              </p>
                              {question.is_reverse_scored === 1 && (
                                <div className="flex items-center space-x-2 bg-yellow-100 border border-yellow-300 rounded-lg px-3 py-2">
                                  <span className="text-lg">🔄</span>
                                  <span className="text-sm font-semibold text-yellow-800">
                                    Đảo điểm (Reverse Scoring)
                                  </span>
                                  <span className="text-xs text-yellow-700">
                                    - Công thức: 6 - điểm gốc
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {currentTestTab === "holland" && (
                            <div className="p-3 bg-emerald-50 rounded-lg">
                              <p className="text-sm text-emerald-700">
                                <strong>RIASEC Type:</strong>{" "}
                                {question.type || "N/A"} |
                                <strong> Scale:</strong> 1-5 Rating (Không thích
                                → Rất thích)
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2 ml-6">
                          <button
                            onClick={() => startEdit(question)}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-xl text-sm transition-all duration-300"
                          >
                            Sửa
                          </button>
                          <button
                            onClick={() => deleteQuestion(question.id)}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-xl text-sm transition-all duration-300"
                          >
                            Xóa
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  // Statistics State
  const [statistics, setStatistics] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });

  // IP Alert Modal State
  const [showIPAlertModal, setShowIPAlertModal] = useState(false);
  const [currentAlertType, setCurrentAlertType] = useState(null); // 'warning' or 'exclude'
  const [markingAsRead, setMarkingAsRead] = useState(false);

  // All Traffic Modal State
  const [showAllTrafficModal, setShowAllTrafficModal] = useState(false);
  const [allTrafficData, setAllTrafficData] = useState(null);
  const [loadingAllTraffic, setLoadingAllTraffic] = useState(false);
  const [trafficSourceFilter, setTrafficSourceFilter] = useState('all'); // 'all', 'Google', 'Direct', 'Facebook', etc.

  // Load statistics
  const loadStatistics = async (customDateFilter = null) => {
    setLoadingStats(true);
    try {
      // Build query params
      const filter = customDateFilter || dateFilter;
      let url = '/api/statistics/password-verifications';
      const params = new URLSearchParams();

      if (filter.startDate) {
        params.append('startDate', filter.startDate);
      }
      if (filter.endDate) {
        params.append('endDate', filter.endDate);
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setStatistics(data);
      }
    } catch (error) {
      console.error('❌ Failed to load statistics:', error);
      showNotification('❌ Lỗi khi tải thống kê', 'error');
    } finally {
      setLoadingStats(false);
    }
  };

  // Load statistics when tab changes to statistics
  useEffect(() => {
    if (currentTab === 'statistics') {
      loadStatistics();
    }
  }, [currentTab]);

  // Handle date filter change
  const handleDateFilterChange = (field, value) => {
    const newFilter = { ...dateFilter, [field]: value };
    setDateFilter(newFilter);
  };

  // Apply date filter
  const applyDateFilter = () => {
    loadStatistics(dateFilter);
  };

  // Clear date filter
  const clearDateFilter = () => {
    const emptyFilter = { startDate: '', endDate: '' };
    setDateFilter(emptyFilter);
    loadStatistics(emptyFilter);
  };

  // View today's data
  const viewTodayData = () => {
    const today = new Date().toISOString().split('T')[0];
    const todayFilter = { startDate: today, endDate: today };
    setDateFilter(todayFilter);
    loadStatistics(todayFilter);
  };

  // Open IP Alert Modal
  const openIPAlertModal = (alertType) => {
    setCurrentAlertType(alertType);
    setShowIPAlertModal(true);
  };

  // Close IP Alert Modal
  const closeIPAlertModal = () => {
    setShowIPAlertModal(false);
    setCurrentAlertType(null);
  };

  // Open All Traffic Modal
  const openAllTrafficModal = async () => {
    setShowAllTrafficModal(true);
    setLoadingAllTraffic(true);

    try {
      const response = await fetch('/api/statistics/all-traffic');
      const data = await response.json();

      if (data.success) {
        setAllTrafficData(data);
      } else {
        showNotification('❌ Lỗi khi tải dữ liệu', 'error');
      }
    } catch (error) {
      console.error('Error loading all traffic data:', error);
      showNotification('❌ Lỗi khi tải dữ liệu', 'error');
    } finally {
      setLoadingAllTraffic(false);
    }
  };

  // Close All Traffic Modal
  const closeAllTrafficModal = () => {
    setShowAllTrafficModal(false);
    setAllTrafficData(null);
  };

  // Mark IP alerts as read
  const markIPAlertsAsRead = async () => {
    if (!currentAlertType || !statistics?.ipAnalysis) return;

    setMarkingAsRead(true);
    try {
      const currentCount = currentAlertType === 'warning'
        ? statistics.ipAnalysis.warning.length
        : statistics.ipAnalysis.exclude.length;

      const response = await fetch('/api/statistics/mark-ip-alerts-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          alertType: currentAlertType,
          currentCount: currentCount
        })
      });

      const data = await response.json();

      if (data.success) {
        showNotification('✅ Đã đánh dấu là đã đọc', 'success');
        // Reload statistics to update badge
        await loadStatistics();
      } else {
        showNotification('❌ Lỗi khi đánh dấu đã đọc', 'error');
      }
    } catch (error) {
      console.error('❌ Failed to mark as read:', error);
      showNotification('❌ Lỗi khi đánh dấu đã đọc', 'error');
    } finally {
      setMarkingAsRead(false);
    }
  };

  // Export IP list to Excel (XLSX format)
  const exportIPListToExcel = () => {
    if (!currentAlertType || !statistics?.ipAnalysis) return;

    const ipList = currentAlertType === 'warning'
      ? statistics.ipAnalysis.warning
      : statistics.ipAnalysis.exclude;

    if (ipList.length === 0) {
      showNotification('⚠️ Không có dữ liệu để xuất', 'warning');
      return;
    }

    // Import XLSX library dynamically
    import('xlsx').then((XLSX) => {
      // Prepare data for Excel
      const headers = ['IP Address', 'Test Type', 'Số lần truy cập', 'Lần đầu', 'Lần cuối'];
      const rows = ipList.map(ip => [
        ip.user_ip,
        ip.test_type,
        ip.access_count,
        new Date(ip.first_access).toLocaleString('vi-VN'),
        new Date(ip.last_access).toLocaleString('vi-VN')
      ]);

      // Create worksheet
      const wsData = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      ws['!cols'] = [
        { wch: 20 }, // IP Address
        { wch: 15 }, // Test Type
        { wch: 18 }, // Số lần truy cập
        { wch: 25 }, // Lần đầu
        { wch: 25 }  // Lần cuối
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, currentAlertType === 'warning' ? 'IP Cảnh Báo' : 'IP Loại Trừ');

      // Generate file name
      const fileName = `IP_${currentAlertType === 'warning' ? 'Canh_Bao' : 'Loai_Tru'}_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download file
      XLSX.writeFile(wb, fileName);

      showNotification('✅ Đã xuất file Excel thành công', 'success');
    }).catch(error => {
      console.error('Error loading XLSX library:', error);
      showNotification('❌ Lỗi khi xuất file Excel', 'error');
    });
  };

  // Statistics
  const renderStatistics = () => {
    const testTypeNames = {
      mbti: 'MBTI',
      iq: 'IQ',
      eq: 'EQ',
      disc: 'DISC',
      holland: 'Holland Codes'
    };

    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-800">Thống kê hệ thống</h2>
          <button
            onClick={() => loadStatistics()}
            disabled={loadingStats}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {loadingStats ? '🔄 Đang tải...' : '🔄 Làm mới'}
          </button>
        </div>

        {/* Date Filter */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-4">
            <div className="text-2xl">📅</div>
            <h3 className="text-xl font-semibold text-gray-800">Lọc theo ngày</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Từ ngày
              </label>
              <input
                type="date"
                value={dateFilter.startDate}
                onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Đến ngày
              </label>
              <input
                type="date"
                value={dateFilter.endDate}
                onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={applyDateFilter}
                disabled={loadingStats}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                🔍 Lọc
              </button>
            </div>

            <div className="flex items-end">
              <button
                onClick={viewTodayData}
                disabled={loadingStats}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                📅 Hôm nay
              </button>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearDateFilter}
                disabled={loadingStats}
                className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                ✖️ Xóa lọc
              </button>
            </div>
          </div>

          {(dateFilter.startDate || dateFilter.endDate) && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Đang lọc:</strong>
                {dateFilter.startDate && ` Từ ${new Date(dateFilter.startDate).toLocaleDateString('vi-VN')}`}
                {dateFilter.endDate && ` đến ${new Date(dateFilter.endDate).toLocaleDateString('vi-VN')}`}
              </p>
            </div>
          )}
        </div>



        {loadingStats ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-4xl mb-4 animate-pulse">⏳</div>
            <p className="text-gray-600">Đang tải thống kê...</p>
          </div>
        ) : statistics ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {statistics.statistics.map((stat) => (
                <div
                  key={stat.test_type}
                  className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-2xl">
                      {stat.test_type === 'mbti' && '🧠'}
                      {stat.test_type === 'iq' && '💡'}
                      {stat.test_type === 'eq' && '❤️'}
                      {stat.test_type === 'disc' && '🎯'}
                      {stat.test_type === 'holland' && '🔧'}
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-600">
                        {stat.unique_users}
                      </div>
                      <div className="text-xs text-gray-500">Người xác thực</div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-gray-700 mb-1">
                    Test {testTypeNames[stat.test_type]}
                  </div>
                  <div className="text-xs text-gray-500">
                    Tổng {stat.total_verifications} lượt xác thực
                  </div>
                </div>
              ))}
            </div>

            {/* ⚠️ IP Analysis - Compact Buttons */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* IP Analysis Buttons (Google Ads only) */}
              {statistics.ipAnalysis && (statistics.ipAnalysis.warning.length > 0 || statistics.ipAnalysis.exclude.length > 0) && (
                <>
                  {/* Exclude List Button */}
                  {statistics.ipAnalysis.exclude.length > 0 && (
                    <button
                      onClick={() => openIPAlertModal('exclude')}
                      className="relative inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium py-2 px-4 rounded-lg shadow-md transition-all hover:shadow-lg"
                    >
                      <span className="text-lg">🚫</span>
                      <span className="text-sm">IP loại trừ</span>
                      <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs font-bold">
                        {statistics.ipAnalysis.exclude.length}
                      </span>
                      {/* Badge for unread */}
                      {statistics.ipAnalysis.readStatus?.exclude?.unread > 0 && (
                        <div className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse shadow-lg ring-2 ring-white">
                          {statistics.ipAnalysis.readStatus.exclude.unread}
                        </div>
                      )}
                    </button>
                  )}

                  {/* Warning List Button */}
                  {statistics.ipAnalysis.warning.length > 0 && (
                    <button
                      onClick={() => openIPAlertModal('warning')}
                      className="relative inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-800 font-medium py-2 px-4 rounded-lg shadow-md transition-all hover:shadow-lg"
                    >
                      <span className="text-lg">⚠️</span>
                      <span className="text-sm">IP chú ý</span>
                      <span className="bg-white/30 px-2 py-0.5 rounded-full text-xs font-bold">
                        {statistics.ipAnalysis.warning.length}
                      </span>
                      {/* Badge for unread */}
                      {statistics.ipAnalysis.readStatus?.warning?.unread > 0 && (
                        <div className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse shadow-lg ring-2 ring-white">
                          {statistics.ipAnalysis.readStatus.warning.unread}
                        </div>
                      )}
                    </button>
                  )}
                </>
              )}

              {/* All Traffic Button (All sources) */}
              <button
                onClick={openAllTrafficModal}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-medium py-2 px-4 rounded-lg shadow-md transition-all hover:shadow-lg"
              >
                <span className="text-lg">📊</span>
                <span className="text-sm">Toàn bộ nguồn</span>
              </button>
            </div>

            {/* IP Alert Modal */}
            {showIPAlertModal && currentAlertType && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                  {/* Modal Header */}
                  <div className={`p-6 ${currentAlertType === 'exclude' ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gradient-to-r from-yellow-400 to-yellow-500'}`}>
                    <div className="flex items-center justify-between text-white">
                      <div className="flex items-center space-x-3">
                        <div className="text-3xl">
                          {currentAlertType === 'exclude' ? '🚫' : '⚠️'}
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold">
                            {currentAlertType === 'exclude' ? 'Danh sách IP cần loại trừ' : 'Danh sách IP cần chú ý'}
                          </h3>
                          <p className="text-sm opacity-90">
                            {currentAlertType === 'exclude'
                              ? 'Các IP đã truy cập ≥3 lần cho cùng 1 loại test'
                              : 'Các IP đã truy cập 2 lần cho cùng 1 loại test'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={closeIPAlertModal}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6 overflow-y-auto max-h-[60vh]">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className={currentAlertType === 'exclude' ? 'bg-orange-50' : 'bg-yellow-50'}>
                          <tr>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${currentAlertType === 'exclude' ? 'text-orange-700' : 'text-yellow-700'}`}>
                              IP Address
                            </th>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${currentAlertType === 'exclude' ? 'text-orange-700' : 'text-yellow-700'}`}>
                              Test Type
                            </th>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${currentAlertType === 'exclude' ? 'text-orange-700' : 'text-yellow-700'}`}>
                              Số lần truy cập
                            </th>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${currentAlertType === 'exclude' ? 'text-orange-700' : 'text-yellow-700'}`}>
                              Lần đầu
                            </th>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${currentAlertType === 'exclude' ? 'text-orange-700' : 'text-yellow-700'}`}>
                              Lần cuối
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {(currentAlertType === 'exclude'
                              ? [...statistics.ipAnalysis.exclude]
                              : [...statistics.ipAnalysis.warning]
                            )
                              .sort((a, b) => {
                                const da = new Date(a.last_access).getTime();
                                const db = new Date(b.last_access).getTime();
                                if (db !== da) return db - da; // newest first
                                return (b.access_count || 0) - (a.access_count || 0);
                              })
                              .map((ip, index) => (
                            <tr key={index} className={`hover:${currentAlertType === 'exclude' ? 'bg-orange-50' : 'bg-yellow-50'}`}>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm font-mono font-bold ${currentAlertType === 'exclude' ? 'text-orange-600' : 'text-yellow-600'}`}>
                                {ip.user_ip}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex flex-wrap gap-1">
                                  {ip.test_types?.split(', ').map((testType, idx) => (
                                    <span key={idx} className={`px-2 py-1 text-xs font-semibold rounded-full ${currentAlertType === 'exclude' ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                      {testTypeNames[testType] || testType.toUpperCase()}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-3 py-1 text-sm font-bold rounded-full ${currentAlertType === 'exclude' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {ip.access_count} lần
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {new Date(ip.first_access).toLocaleString('vi-VN')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {new Date(ip.last_access).toLocaleString('vi-VN')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
                    <div className="text-sm text-gray-600">
                      Tổng số: <span className="font-bold">{currentAlertType === 'exclude' ? statistics.ipAnalysis.exclude.length : statistics.ipAnalysis.warning.length}</span> IP
                      {statistics.ipAnalysis.readStatus?.[currentAlertType]?.lastReadAt && (
                        <span className="ml-4">
                          Đọc lần cuối: <span className="font-semibold">{new Date(statistics.ipAnalysis.readStatus[currentAlertType].lastReadAt).toLocaleString('vi-VN')}</span>
                        </span>
                      )}
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={exportIPListToExcel}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg transition-colors flex items-center space-x-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Xuất Excel</span>
                      </button>
                      <button
                        onClick={markIPAlertsAsRead}
                        disabled={markingAsRead}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-6 rounded-lg transition-colors flex items-center space-x-2"
                      >
                        {markingAsRead ? (
                          <>
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Đang xử lý...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Đánh dấu đã đọc</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={closeIPAlertModal}
                        className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg transition-colors"
                      >
                        Đóng
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* All Traffic Modal */}
            {showAllTrafficModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
                  {/* Modal Header */}
                  <div className="p-6 bg-gradient-to-r from-blue-500 to-indigo-500">
                    <div className="flex items-center justify-between text-white">
                      <div className="flex items-center space-x-3">
                        <div className="text-3xl">📊</div>
                        <div>
                          <h3 className="text-2xl font-bold">Thống kê toàn bộ nguồn truy cập</h3>
                          <p className="text-sm opacity-90">Phân tích lưu lượng từ tất cả các nguồn (Google, Direct, Facebook,...)</p>
                        </div>
                      </div>
                      <button
                        onClick={closeAllTrafficModal}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6 overflow-y-auto max-h-[70vh]">
                    {loadingAllTraffic ? (
                      <div className="flex items-center justify-center py-12">
                        <svg className="animate-spin h-12 w-12 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    ) : allTrafficData ? (
                      <div className="space-y-6">
                        {/* Source Statistics */}
                        <div>
                          <h4 className="text-lg font-semibold text-gray-800 mb-3">📈 Thống kê theo nguồn</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {allTrafficData.sourceStats.map((source, index) => (
                              <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-semibold text-gray-700">{source.source_category}</span>
                                  <span className="text-2xl">
                                    {source.source_category === 'Google' ? '🔍' :
                                     source.source_category === 'Direct' ? '🔗' :
                                     source.source_category === 'Facebook' ? '📘' :
                                     source.source_category === 'Instagram' ? '📷' :
                                     source.source_category === 'TikTok' ? '🎵' : '🌐'}
                                  </span>
                                </div>
                                <div className="space-y-1">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Tổng lượt:</span>
                                    <span className="font-bold text-blue-600">{source.total_verifications}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">IP duy nhất:</span>
                                    <span className="font-bold text-indigo-600">{source.unique_ips}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* IP Frequency Table */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-lg font-semibold text-gray-800">🔄 Chi tiết truy cập theo IP</h4>
                            {/* Source Filter Buttons */}
                            <div className="flex gap-2">
                              <button
                                onClick={() => setTrafficSourceFilter('all')}
                                className={`px-3 py-1 text-sm font-semibold rounded-lg transition-colors ${
                                  trafficSourceFilter === 'all'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                Tất cả
                              </button>
                              {allTrafficData.sourceStats.map((source, index) => (
                                <button
                                  key={index}
                                  onClick={() => setTrafficSourceFilter(source.source_category)}
                                  className={`px-3 py-1 text-sm font-semibold rounded-lg transition-colors ${
                                    trafficSourceFilter === source.source_category
                                      ? 'bg-blue-500 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  {source.source_category === 'Google' ? '🔍' :
                                   source.source_category === 'Direct' ? '🔗' :
                                   source.source_category === 'Facebook' ? '📘' :
                                   source.source_category === 'Instagram' ? '📷' :
                                   source.source_category === 'TikTok' ? '🎵' : '🌐'} {source.source_category}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-blue-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">IP Address</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Nguồn</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Số lần</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Test Types</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Lần đầu</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Lần cuối</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {allTrafficData.ipFrequency
                                  .filter(ip => {
                                    if (trafficSourceFilter === 'all') return true;
                                    // Map referrer_source to source_category
                                    const sourceCategory =
                                      ip.referrer_source === 'direct' ? 'Direct' :
                                      ip.referrer_source.includes('google') ? 'Google' :
                                      ip.referrer_source.includes('facebook') ? 'Facebook' :
                                      ip.referrer_source.includes('instagram') ? 'Instagram' :
                                      ip.referrer_source.includes('tiktok') ? 'TikTok' : 'Other';
                                    return sourceCategory === trafficSourceFilter;
                                  })
                                  .map((ip, index) => (
                                  <tr key={index} className="hover:bg-blue-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-bold text-blue-600">
                                      {ip.user_ip}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                      {ip.referrer_source === 'direct' ? '🔗 Direct' :
                                       ip.referrer_source.includes('google') ? '🔍 Google' :
                                       ip.referrer_source.includes('facebook') ? '📘 Facebook' :
                                       ip.referrer_source}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`px-3 py-1 text-sm font-bold rounded-full ${
                                        ip.access_count >= 5 ? 'bg-red-100 text-red-700' :
                                        ip.access_count >= 3 ? 'bg-orange-100 text-orange-700' :
                                        'bg-yellow-100 text-yellow-700'
                                      }`}>
                                        {ip.access_count} lần
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                      {ip.test_types}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                      {new Date(ip.first_access).toLocaleString('vi-VN')}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                      {new Date(ip.last_access).toLocaleString('vi-VN')}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        Không có dữ liệu
                      </div>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
                    <div className="text-sm text-gray-600">
                      {allTrafficData && (
                        <>
                          {trafficSourceFilter === 'all' ? (
                            <>Tổng số IP: <span className="font-bold">{allTrafficData.ipFrequency.length}</span></>
                          ) : (
                            <>
                              IP từ {trafficSourceFilter}: <span className="font-bold">
                                {allTrafficData.ipFrequency.filter(ip => {
                                  const sourceCategory =
                                    ip.referrer_source === 'direct' ? 'Direct' :
                                    ip.referrer_source.includes('google') ? 'Google' :
                                    ip.referrer_source.includes('facebook') ? 'Facebook' :
                                    ip.referrer_source.includes('instagram') ? 'Instagram' :
                                    ip.referrer_source.includes('tiktok') ? 'TikTok' : 'Other';
                                  return sourceCategory === trafficSourceFilter;
                                }).length}
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    <button
                      onClick={closeAllTrafficModal}
                      className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg transition-colors"
                    >
                      Đóng
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Verifications Table */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                📋 Lịch sử xác thực gần đây (50 lượt cuối)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Test Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nguồn truy cập
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Thời gian
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {statistics.recentVerifications.map((verification, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {testTypeNames[verification.test_type]}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {(verification.is_google_ads === 1 || (verification.referrer_source || '').includes('google')) ? (
                            <span className="text-green-600 font-medium">🔍 Google</span>
                          ) : (
                            verification.referrer_source || 'unknown'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                          {verification.user_ip}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(verification.verified_at).toLocaleString('vi-VN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-xl font-semibold mb-2">Chưa có dữ liệu thống kê</h3>
            <p className="text-gray-600">
              Click "Làm mới" để tải thống kê
            </p>
          </div>
        )}
      </div>
    );
  };

  // Page Management - SEO for each page

  const saveSeoSettings = async () => {
    try {
      await seoAPI.updateSettings(seoSettings);
      // Persist the latest SEO settings to localStorage so they stay after page reloads
      localStorage.setItem("seo_settings", JSON.stringify(seoSettings));
      showNotification("📈 Cài đặt SEO đã được lưu thành công!", "success");
    } catch (error) {
      console.error("Error saving SEO settings:", error);
      showNotification("❌ Lỗi khi lưu cài đặt SEO: " + error.message, "error");
    }
  };

  const renderPageManagement = () => {
    const pageList = [
      {
        key: "home",
        name: "Trang chủ",
        path: "/",
        description: "Trang chính của website với danh sách các bài test",
      },
      {
        key: "about",
        name: "Giới thiệu",
        path: "/about",
        description: "Trang giới thiệu về các loại test tâm lý",
      },
      {
        key: "mbti",
        name: "Test MBTI",
        path: "/test-mbti",
        description: "Trang test tính cách MBTI 16 nhóm",
      },
      {
        key: "iq",
        name: "Test IQ",
        path: "/test-iq",
        description: "Trang test chỉ số thông minh IQ",
      },
      {
        key: "eq",
        name: "Test EQ",
        path: "/test-eq",
        description: "Trang test trí tuệ cảm xúc EQ",
      },
      {
        key: "disc",
        name: "Test DISC",
        path: "/test-disc",
        description: "Trang test phong cách hành vi DISC",
      },
      {
        key: "holland",
        name: "Holland Code",
        path: "/holland-codes",
        description: "Trang test sở thích nghề nghiệp Holland",
      },
    ];

    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-800">
            Quản lý SEO trang
          </h2>
          <button
            onClick={saveSeoSettings}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors"
          >
            Lưu tất cả
          </button>
        </div>

        {/* Page List Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trang
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageList.map((page) => {
                const pageData = seoSettings[page.key] || {
                  title: "",
                  h1: "",
                  metaDescription: "",
                };

                const isComplete =
                  pageData.title && pageData.h1 && pageData.metaDescription;
                const isEditing = editingPage === page.key;

                return (
                  <React.Fragment key={page.key}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {page.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {page.description}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                          {page.path}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            isComplete
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {isComplete ? "Hoàn thành" : "Chưa hoàn thành"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() =>
                            setEditingPage(isEditing ? null : page.key)
                          }
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          {isEditing ? "Thu gọn" : "Chỉnh sửa"}
                        </button>
                      </td>
                    </tr>

                    {isEditing && (
                      <tr>
                        <td colSpan={4}>
                          <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
                            {/* Title Tag */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Title Tag (Browser Tab)
                              </label>
                              <input
                                type="text"
                                value={pageData.title}
                                onChange={(e) =>
                                  setSeoSettings({
                                    ...seoSettings,
                                    [page.key]: {
                                      ...pageData,
                                      title: e.target.value,
                                    },
                                  })
                                }
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Tiêu đề hiển thị trên tab browser..."
                              />
                            </div>
                            {/* H1 Tag */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                H1 Heading (Tiêu đề chính)
                              </label>
                              <input
                                type="text"
                                value={pageData.h1}
                                onChange={(e) =>
                                  setSeoSettings({
                                    ...seoSettings,
                                    [page.key]: {
                                      ...pageData,
                                      h1: e.target.value,
                                    },
                                  })
                                }
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                placeholder="Tiêu đề chính của trang..."
                              />
                            </div>
                            {/* Meta Description */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Meta Description (Google Search)
                              </label>
                              <textarea
                                rows={3}
                                value={pageData.metaDescription}
                                onChange={(e) =>
                                  setSeoSettings({
                                    ...seoSettings,
                                    [page.key]: {
                                      ...pageData,
                                      metaDescription: e.target.value,
                                    },
                                  })
                                }
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                                placeholder="Mô tả ngắn gọn hiển thị trên Google search..."
                              />
                            </div>
                            {/* Buttons */}
                            <div className="pt-4 border-t border-gray-200 flex space-x-4">
                              <button
                                onClick={() => {
                                  saveSeoSettings();
                                  showNotification(
                                    `SEO cho trang "${page.name}" đã được lưu!`,
                                    "success"
                                  );
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg"
                              >
                                Lưu thay đổi
                              </button>
                              <button
                                onClick={() => setEditingPage(null)}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-6 rounded-lg"
                              >
                                Hủy
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Settings Management
  const renderSettings = () => {
    const saveTestPasswords = async () => {
      try {
        await testPasswordsAPI.update(testPasswords);
        localStorage.setItem("test_passwords", JSON.stringify(testPasswords));
        showNotification("🔐 Mật khẩu đã được cập nhật thành công!", "success");
      } catch (error) {
        console.error("❌ Failed to save test passwords:", error);
        showNotification("❌ Lỗi khi lưu mật khẩu: " + error.message, "error");
      }
    };

    const saveHeaderFooterSettings = async () => {
      try {
        await headerFooterAPI.update(headerFooterSettings);
        localStorage.setItem(
          "header_footer_settings",
          JSON.stringify(headerFooterSettings)
        );

        // Apply to document
        if (
          headerFooterSettings.enable_head &&
          headerFooterSettings.head_code
        ) {
          const existingHead = document.getElementById("custom-head-code");
          if (existingHead) existingHead.remove();
          const headScript = document.createElement("div");
          headScript.id = "custom-head-code";
          headScript.innerHTML = headerFooterSettings.head_code;
          document.head.appendChild(headScript);
        }

        if (
          headerFooterSettings.enable_body &&
          headerFooterSettings.body_code
        ) {
          const existingBody = document.getElementById("custom-body-code");
          if (existingBody) existingBody.remove();
          const bodyScript = document.createElement("div");
          bodyScript.id = "custom-body-code";
          bodyScript.innerHTML = headerFooterSettings.body_code;
          document.body.insertBefore(bodyScript, document.body.firstChild);
        }

        if (
          headerFooterSettings.enable_footer &&
          headerFooterSettings.footer_code
        ) {
          const existingFooter = document.getElementById("custom-footer-code");
          if (existingFooter) existingFooter.remove();
          const footerScript = document.createElement("div");
          footerScript.id = "custom-footer-code";
          footerScript.innerHTML = headerFooterSettings.footer_code;
          document.body.appendChild(footerScript);
        }

        showNotification(
          "📄 Code Head/Body/Footer đã được áp dụng!",
          "success"
        );
      } catch (error) {
        console.error("❌ Failed to save header/footer settings:", error);
        showNotification("❌ Lỗi khi lưu cài đặt: " + error.message, "error");
      }
    };

    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-800">Cài đặt hệ thống</h2>
        </div>

        {/* Test Passwords Management */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Quản lý mật khẩu test
            </h3>
            <p className="text-gray-600">
              Thay đổi mật khẩu để xem kết quả của từng loại test
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testTabs.map((test) => (
              <div key={test.id} className="space-y-3">
                <label className="block">
                  <span className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <span>{test.label}</span>
                  </span>
                  <input
                    type="text"
                    value={testPasswords[test.id]}
                    onChange={(e) =>
                      setTestPasswords({
                        ...testPasswords,
                        [test.id]: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                    placeholder={`Mật khẩu cho ${test.label}`}
                  />
                </label>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t">
            <button
              onClick={saveTestPasswords}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              Lưu mật khẩu
            </button>
          </div>
        </div>

        {/* Admin Password Management */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              🔐 Đổi mật khẩu Admin
            </h3>
            <p className="text-gray-600">
              Thay đổi mật khẩu đăng nhập admin để bảo mật hệ thống
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mật khẩu hiện tại:
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                placeholder="Nhập mật khẩu hiện tại..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mật khẩu mới:
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                placeholder="Nhập mật khẩu mới..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Xác nhận mật khẩu mới:
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                placeholder="Nhập lại mật khẩu mới..."
              />
            </div>

            <div className="pt-4">
              <button
                onClick={changeAdminPassword}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105"
              >
                🔐 Đổi mật khẩu Admin
              </button>
            </div>

            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Lưu ý bảo mật:</strong> Hệ thống cho phép tối đa 3
                lần nhập sai mật khẩu. Nếu nhập sai quá 3 lần, bạn cần liên hệ
                admin để reset lại số lần đăng nhập.
              </p>
            </div>
          </div>
        </div>

        {/* Header Footer Code Management */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Quản lý Head, Body & Footer Code
            </h3>
            <p className="text-gray-600">
              Thêm code HTML/JavaScript vào các vị trí khác nhau của website
              (tương tự plugin Header Footer)
            </p>
          </div>

          <div className="space-y-6">
            {/* Head Code */}
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  id="enable_head"
                  checked={headerFooterSettings.enable_head}
                  onChange={(e) =>
                    setHeaderFooterSettings({
                      ...headerFooterSettings,
                      enable_head: e.target.checked,
                    })
                  }
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="enable_head"
                  className="text-lg font-semibold text-gray-800"
                >
                  Head Code (trong &lt;head&gt;&lt;/head&gt;)
                </label>
              </div>
              <textarea
                value={headerFooterSettings.head_code}
                onChange={(e) =>
                  setHeaderFooterSettings({
                    ...headerFooterSettings,
                    head_code: e.target.value,
                  })
                }
                disabled={!headerFooterSettings.enable_head}
                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 font-mono text-sm disabled:bg-gray-100"
                placeholder="<!-- Google Analytics, Meta tags, CSS, v.v... -->"
              />
            </div>

            {/* Body Code */}
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  id="enable_body"
                  checked={headerFooterSettings.enable_body}
                  onChange={(e) =>
                    setHeaderFooterSettings({
                      ...headerFooterSettings,
                      enable_body: e.target.checked,
                    })
                  }
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="enable_body"
                  className="text-lg font-semibold text-gray-800"
                >
                  Body Code (đầu &lt;body&gt;)
                </label>
              </div>
              <textarea
                value={headerFooterSettings.body_code}
                onChange={(e) =>
                  setHeaderFooterSettings({
                    ...headerFooterSettings,
                    body_code: e.target.value,
                  })
                }
                disabled={!headerFooterSettings.enable_body}
                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 font-mono text-sm disabled:bg-gray-100"
                placeholder="<!-- Google Tag Manager, Popup, Notification, v.v... -->"
              />
            </div>

            {/* Footer Code */}
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  id="enable_footer"
                  checked={headerFooterSettings.enable_footer}
                  onChange={(e) =>
                    setHeaderFooterSettings({
                      ...headerFooterSettings,
                      enable_footer: e.target.checked,
                    })
                  }
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="enable_footer"
                  className="text-lg font-semibold text-gray-800"
                >
                  Footer Code (cuối &lt;/body&gt;)
                </label>
              </div>
              <textarea
                value={headerFooterSettings.footer_code}
                onChange={(e) =>
                  setHeaderFooterSettings({
                    ...headerFooterSettings,
                    footer_code: e.target.value,
                  })
                }
                disabled={!headerFooterSettings.enable_footer}
                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 font-mono text-sm disabled:bg-gray-100"
                placeholder="<!-- Chat widget, Analytics tracking, v.v... -->"
              />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t">
            <button
              onClick={saveHeaderFooterSettings}
              className="relative bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 hover:from-emerald-600 hover:via-green-600 hover:to-teal-600 text-white font-bold py-4 px-10 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-green-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              <div className="relative flex items-center space-x-3">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span>✨ Áp dụng & Lưu Code</span>
                <svg
                  className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* Usage Guide */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">
            📋 Hướng dẫn sử dụng
          </h4>
          <div className="text-sm text-gray-700">
            <h5 className="font-semibold mb-2">💻 Head/Body/Footer Code:</h5>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                <strong>Head Code:</strong> Google Analytics, Meta tags, CSS tùy
                chỉnh
              </li>
              <li>
                <strong>Body Code:</strong> Google Tag Manager, Popup,
                Notification
              </li>
              <li>
                <strong>Footer Code:</strong> Chat widget, Tracking scripts,
                JavaScript
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Main Admin Panel
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification */}
      {notification.show && (
        <div
          className={`fixed top-4 right-4 z-[100] p-4 rounded-xl shadow-lg border-l-4 max-w-md transform transition-all duration-300 ${
            notification.type === "success"
              ? "bg-green-50 border-green-500 text-green-800"
              : notification.type === "error"
              ? "bg-red-50 border-red-500 text-red-800"
              : notification.type === "warning"
              ? "bg-yellow-50 border-yellow-500 text-yellow-800"
              : "bg-blue-50 border-blue-500 text-blue-800"
          }`}
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              {notification.type === "success" && (
                <svg
                  className="w-5 h-5 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {notification.type === "error" && (
                <svg
                  className="w-5 h-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {notification.type === "warning" && (
                <svg
                  className="w-5 h-5 text-yellow-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {notification.type === "info" && (
                <svg
                  className="w-5 h-5 text-blue-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() =>
                setNotification((prev) => ({ ...prev, show: false }))
              }
              className={`flex-shrink-0 p-1 rounded-full hover:bg-opacity-20 ${
                notification.type === "success"
                  ? "hover:bg-green-200"
                  : notification.type === "error"
                  ? "hover:bg-red-200"
                  : notification.type === "warning"
                  ? "hover:bg-yellow-200"
                  : "hover:bg-blue-200"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-64 bg-white shadow-xl z-50">
        <div className="p-6">
          <div className="flex items-center mb-8">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white text-lg mr-3">
              🔧
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Admin Panel</h1>
              <p className="text-sm text-gray-500">IQ Smart System</p>
            </div>
          </div>

          <nav className="space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setCurrentTab(tab.id);
                  localStorage.setItem("admin_current_tab", tab.id);
                  scrollToTop(); // Scroll to top when changing tabs
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                  currentTab === tab.id
                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="absolute bottom-6 left-6 right-6">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-all duration-300"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span>Đăng xuất</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        {currentTab === "dashboard" && renderDashboard()}
        {currentTab === "questions" && renderQuestionManagement()}
        {currentTab === "pages" && renderPageManagement()}
        {currentTab === "content" && renderContentManagement()}
        {currentTab === "statistics" && renderStatistics()}
        {currentTab === "settings" && renderSettings()}
      </div>

      {/* Activity Log Modal */}
      {showActivityLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gray-800 text-white p-5 flex justify-between items-center border-b border-gray-700">
              <div>
                <h2 className="text-xl font-semibold">📋 Lịch sử hoạt động</h2>
                <p className="text-gray-300 text-sm mt-1">
                  {testTabs.find(t => t.id === selectedTest)?.label} • {activityLogTotal} bản ghi
                </p>
              </div>
              <button
                onClick={() => setShowActivityLog(false)}
                className="text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Date Filter */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Lọc theo ngày:</label>
                <select
                  value={activityLogDateFilter}
                  onChange={(e) => setActivityLogDateFilter(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800"
                >
                  <option value="all">Tất cả</option>
                  <option value="today">Hôm nay</option>
                  <option value="yesterday">Hôm qua</option>
                  <option value="last7days">7 ngày qua</option>
                  <option value="custom">Chọn ngày cụ thể</option>
                </select>
                <input
                  type="date"
                  value={activityLogCustomDate}
                  onChange={(e) => {
                    setActivityLogCustomDate(e.target.value);
                    if (e.target.value) {
                      setActivityLogDateFilter('custom');
                    }
                  }}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800"
                  placeholder="Chọn ngày cụ thể"
                />
              </div>
            </div>

            {/* Content */}
            <div className="p-5 overflow-y-auto max-h-[calc(90vh-240px)] bg-gray-50">
              {loadingActivityLog ? (
                <div className="flex justify-center items-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-800"></div>
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-base font-medium">Chưa có lịch sử hoạt động</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activityLogs.map((log) => {
                    const activityTypeLabels = {
                      template_switch: { label: 'Chuyển mẫu tự động', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: '🔄' },
                      manual_change: { label: 'Thay đổi thủ công', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: '✏️' },
                      set_active_template: { label: 'Đặt làm mẫu hiển thị', color: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: '⭐' },
                      daily_reset: { label: 'Reset ngày mới', color: 'bg-green-50 text-green-700 border-green-200', icon: '🌅' },
                      lastResetTime_init: { label: 'Khởi tạo thời gian', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: '⚙️' },
                      config_update: { label: 'Cập nhật cấu hình', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: '🔧' }
                    };
                    const typeInfo = activityTypeLabels[log.activity_type] || { label: log.activity_type, color: 'bg-gray-50 text-gray-700 border-gray-200', icon: '📝' };

                    return (
                      <div key={log.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{typeInfo.icon}</span>
                            <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${typeInfo.color}`}>
                              {typeInfo.label}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 font-mono">
                            {(() => {
                              // Backend trả về format: YYYY-MM-DD HH:mm:ss (UTC+7)
                              const timeString = log.created_at_utc7;
                              const match = timeString.match(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
                              if (match) {
                                const [, year, month, day, hour, minute, second] = match;
                                return `${hour}:${minute}:${second} ${day}/${month}/${year}`;
                              }
                              return timeString;
                            })()}
                          </span>
                        </div>

                        {log.from_template_id && log.to_template_id && (
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono text-gray-700">
                              {log.details?.fromTemplateName || log.from_template_id}
                            </span>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            <span className="px-2 py-1 bg-gray-800 text-white rounded text-xs font-mono">
                              {log.details?.toTemplateName || log.to_template_id}
                            </span>
                          </div>
                        )}

                        {log.verification_count !== null && log.quota !== null && (
                          <div className="text-xs text-gray-600 mb-2 bg-gray-50 px-2 py-1 rounded inline-block">
                            Lượt xác thực: <span className="font-semibold text-gray-800">{log.verification_count}/{log.quota}</span>
                          </div>
                        )}

                        {log.details?.reason && (
                          <div className="text-xs text-gray-700 bg-gray-50 rounded p-2.5 border-l-2 border-gray-300">
                            <strong className="text-gray-800">Lý do:</strong> {log.details.reason}
                          </div>
                        )}

                        {/* Show additional details for specific changes */}
                        {log.details?.oldName && log.details?.newName && (
                          <div className="text-xs text-gray-600 mt-2 bg-blue-50 rounded p-2 border-l-2 border-blue-300">
                            <strong className="text-blue-800">Đổi tên:</strong> "{log.details.oldName}" → "{log.details.newName}"
                          </div>
                        )}

                        {/* Show full details in a user-friendly way */}
                        {Object.keys(log.details || {}).length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-800 font-medium">
                              Chi tiết đầy đủ ▼
                            </summary>
                            <div className="mt-2 text-xs bg-gray-100 p-3 rounded border border-gray-200 space-y-2">
                              {log.details.fromTemplateName && (
                                <div className="flex items-start gap-2">
                                  <span className="font-semibold text-gray-700 min-w-[120px]">Từ mẫu:</span>
                                  <span className="text-gray-600">{log.details.fromTemplateName}</span>
                                </div>
                              )}
                              {log.details.toTemplateName && (
                                <div className="flex items-start gap-2">
                                  <span className="font-semibold text-gray-700 min-w-[120px]">Đến mẫu:</span>
                                  <span className="text-gray-600">{log.details.toTemplateName}</span>
                                </div>
                              )}
                              {log.details.switchTimestamp && (
                                <div className="flex items-start gap-2">
                                  <span className="font-semibold text-gray-700 min-w-[120px]">Thời gian chuyển:</span>
                                  <span className="text-gray-600">
                                    {new Date(log.details.switchTimestamp).toLocaleString('vi-VN', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit'
                                    })}
                                  </span>
                                </div>
                              )}
                              {log.details.lastResetTime && (
                                <div className="flex items-start gap-2">
                                  <span className="font-semibold text-gray-700 min-w-[120px]">Lần reset cuối:</span>
                                  <span className="text-gray-600">
                                    {new Date(log.details.lastResetTime).toLocaleString('vi-VN', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit'
                                    })}
                                  </span>
                                </div>
                              )}
                              {log.details.iterations !== undefined && (
                                <div className="flex items-start gap-2">
                                  <span className="font-semibold text-gray-700 min-w-[120px]">Số lần lặp:</span>
                                  <span className="text-gray-600">{log.details.iterations}</span>
                                </div>
                              )}
                              {/* Show other details that are not already displayed */}
                              {Object.entries(log.details).map(([key, value]) => {
                                // Skip already displayed fields
                                if (['fromTemplateName', 'toTemplateName', 'switchTimestamp', 'lastResetTime', 'iterations', 'reason', 'oldName', 'newName'].includes(key)) {
                                  return null;
                                }
                                return (
                                  <div key={key} className="flex items-start gap-2">
                                    <span className="font-semibold text-gray-700 min-w-[120px]">{key}:</span>
                                    <span className="text-gray-600 break-all">
                                      {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            {activityLogTotal > 20 && (
              <div className="border-t border-gray-200 bg-white p-4 flex justify-between items-center">
                <button
                  onClick={() => loadActivityLog(activityLogPage - 1)}
                  disabled={activityLogPage === 0}
                  className="px-4 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ← Trang trước
                </button>
                <span className="text-sm text-gray-600 font-medium">
                  Trang {activityLogPage + 1} / {Math.ceil(activityLogTotal / 20)}
                </span>
                <button
                  onClick={() => loadActivityLog(activityLogPage + 1)}
                  disabled={(activityLogPage + 1) * 20 >= activityLogTotal}
                  className="px-4 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Trang sau →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionManager;
